import React from 'react';
import loadable from '@loadable/component';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const CMSPage = loadable(() =>
  import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage')
);
const LandingPage = loadable(() =>
  import(
    /* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'
  )
);
const HomePreviewPage = loadable(() =>
  import(
    /* webpackChunkName: "HomePreviewPage" */ '../containers/HomePreviewPage/HomePreviewPage'
  )
);
const BlogHubPage = loadable(() =>
  import(/* webpackChunkName: "BlogHubPage" */ '../containers/BlogHubPage/BlogHubPage')
);
const BlogArticlePage = loadable(() =>
  import(
    /* webpackChunkName: "BlogArticlePage" */ '../containers/BlogArticlePage/BlogArticlePage'
  )
);
const WishesPage = loadable(() =>
  import(/* webpackChunkName: "WishesPage" */ '../containers/WishesPage/WishesPage')
);
const CatalogPage = loadable(() =>
  import(/* webpackChunkName: "CatalogPage" */ '../containers/CatalogPage/CatalogPage')
);
const CustomerAccountPage = loadable(() =>
  import(
    /* webpackChunkName: "CustomerAccountPage" */ '../containers/CustomerAccountPage/CustomerAccountPage'
  )
);
const ShopPortalPage = loadable(() =>
  import(
    /* webpackChunkName: "ShopPortalPage" */ '../containers/ShopPortalPage/ShopPortalPage'
  )
);
const CatalogCartPage = loadable(() =>
  import(
    /* webpackChunkName: "CatalogCartPage" */ '../containers/CatalogCartPage/CatalogCartPage'
  )
);
const CatalogCheckoutPage = loadable(() =>
  import(
    /* webpackChunkName: "CatalogCheckoutPage" */ '../containers/CatalogCheckoutPage/CatalogCheckoutPage'
  )
);
const CatalogAdminPage = loadable(() =>
  import(
    /* webpackChunkName: "CatalogAdminPage" */ '../containers/CatalogAdminPage/CatalogAdminPage'
  )
);
const ListingPageCoverPhoto = loadable(() =>
  import(
    /* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'
  )
);
const ListingPageCarousel = loadable(() =>
  import(
    /* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'
  )
);
const PrivacyPolicyPage = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'
  )
);
const SearchPageWithMap = loadable(() =>
  import(
    /* webpackChunkName: "SearchPageWithMap" */ /* webpackPrefetch: true */ '../containers/SearchPage/SearchPageWithMap'
  )
);
const SearchPageWithGrid = loadable(() =>
  import(
    /* webpackChunkName: "SearchPageWithGrid" */ /* webpackPrefetch: true */ '../containers/SearchPage/SearchPageWithGrid'
  )
);
const SeoLocationPage = loadable(() =>
  import(
    /* webpackChunkName: "SeoLocationPage" */ '../containers/SeoLocationPage/SeoLocationPage'
  )
);
const TermsOfServicePage = loadable(() =>
  import(
    /* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'
  )
);
const NoAccessPage = loadable(() =>
  import(
    /* webpackChunkName: "NoAccessPage" */ '../containers/NoAccessPage/NoAccessPage'
  )
);

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() =>
  import(
    /* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'
  )
);

export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
];

const homePreviewSeoPageId = 'homePreviewPage';
const partnersSeoPageId = 'partners';
const flowerDeliveryHubSeoPageId = 'flowerDeliveryHub';
const giftDeliveryHubSeoPageId = 'giftDeliveryHub';
const gourmetSetsDeliveryHubSeoPageId = 'gourmetSetsDeliveryHub';
const mississaugaGiftDeliveryHubSeoPageId = 'mississaugaGiftDeliveryHub';
const torontoGiftDeliveryHubSeoPageId = 'torontoGiftDeliveryHub';
const portCreditSeoPageId = 'portCreditFlowerDelivery';
const mississaugaFlowerDeliverySeoPageId = 'mississaugaFlowerDelivery';
const torontoFlowerDeliverySeoPageId = 'torontoFlowerDelivery';
const vaughanFlowerDeliverySeoPageId = 'vaughanFlowerDelivery';
const markhamFlowerDeliverySeoPageId = 'markhamFlowerDelivery';
const richmondHillFlowerDeliverySeoPageId = 'richmondHillFlowerDelivery';
const bramptonFlowerDeliverySeoPageId = 'bramptonFlowerDelivery';
const burlingtonFlowerDeliverySeoPageId = 'burlingtonFlowerDelivery';
const northYorkFlowerDeliverySeoPageId = 'northYorkFlowerDelivery';
const scarboroughFlowerDeliverySeoPageId = 'scarboroughFlowerDelivery';
const oakvilleFlowerDeliverySeoPageId = 'oakvilleFlowerDelivery';
const etobicokeFlowerDeliverySeoPageId = 'etobicokeFlowerDelivery';
const clarksonFlowerDeliverySeoPageId = 'clarksonFlowerDelivery';
const lakeviewFlowerDeliverySeoPageId = 'lakeviewFlowerDelivery';
const cooksvilleFlowerDeliverySeoPageId = 'cooksvilleFlowerDelivery';
const streetsvilleFlowerDeliverySeoPageId = 'streetsvilleFlowerDelivery';
const erinMillsFlowerDeliverySeoPageId = 'erinMillsFlowerDelivery';
const mississaugaCakesDeliverySeoPageId = 'mississaugaCakesDelivery';
const mississaugaChocolateCoveredStrawberriesDeliverySeoPageId =
  'mississaugaChocolateCoveredStrawberriesDelivery';
const mississaugaBalloonsDeliverySeoPageId = 'mississaugaBalloonsDelivery';
const mississaugaBalloonArchDecorSeoPageId = 'mississaugaBalloonArchDecor';
const mississaugaGourmetSetsDeliverySeoPageId = 'mississaugaGourmetSetsDelivery';
const mississaugaIndoorPlantsDeliverySeoPageId = 'mississaugaIndoorPlantsDelivery';
const mississaugaBirthdayFlowersSeoPageId = 'mississaugaBirthdayFlowers';
const mississaugaSympathyFlowersSeoPageId = 'mississaugaSympathyFlowers';
const mississaugaAnniversaryFlowersSeoPageId = 'mississaugaAnniversaryFlowers';
const mississaugaMothersDayFlowersSeoPageId = 'mississaugaMothersDayFlowers';
const mississaugaValentinesDayFlowersSeoPageId = 'mississaugaValentinesDayFlowers';
const mississaugaGetWellFlowersSeoPageId = 'mississaugaGetWellFlowers';
const mississaugaWeddingFlowersSeoPageId = 'mississaugaWeddingFlowers';
const torontoBirthdayFlowersSeoPageId = 'torontoBirthdayFlowers';
const torontoAnniversaryFlowersSeoPageId = 'torontoAnniversaryFlowers';
const torontoMothersDayFlowersSeoPageId = 'torontoMothersDayFlowers';
const torontoGetWellFlowersSeoPageId = 'torontoGetWellFlowers';
const torontoThankYouFlowersSeoPageId = 'torontoThankYouFlowers';
const torontoWeddingFlowersSeoPageId = 'torontoWeddingFlowers';
const torontoCakesDeliverySeoPageId = 'torontoCakesDelivery';
const torontoBentoCakesDeliverySeoPageId = 'torontoBentoCakesDelivery';
const torontoChocolateCoveredStrawberriesDeliverySeoPageId =
  'torontoChocolateCoveredStrawberriesDelivery';
const torontoBalloonsDeliverySeoPageId = 'torontoBalloonsDelivery';
const torontoBalloonArchDecorSeoPageId = 'torontoBalloonArchDecor';
const torontoBalloonGarlandDecorSeoPageId = 'torontoBalloonGarlandDecor';
const torontoBirthdayBalloonDecorSeoPageId = 'torontoBirthdayBalloonDecor';
const torontoBabyShowerDecorSeoPageId = 'torontoBabyShowerDecor';
const torontoBabyBoyShowerDecorSeoPageId = 'torontoBabyBoyShowerDecor';
const torontoBabyGirlShowerDecorSeoPageId = 'torontoBabyGirlShowerDecor';
const torontoBridalShowerDecorSeoPageId = 'torontoBridalShowerDecor';
const torontoGourmetSetsDeliverySeoPageId = 'torontoGourmetSetsDelivery';
const torontoCorporateGiftBasketsDeliverySeoPageId = 'torontoCorporateGiftBasketsDelivery';
const torontoChristmasGiftBasketsDeliverySeoPageId = 'torontoChristmasGiftBasketsDelivery';
const torontoThankYouGiftBasketsDeliverySeoPageId = 'torontoThankYouGiftBasketsDelivery';
const torontoHousewarmingGiftBasketsDeliverySeoPageId = 'torontoHousewarmingGiftBasketsDelivery';
const torontoIndoorPlantsDeliverySeoPageId = 'torontoIndoorPlantsDelivery';
const torontoRoseDeliverySeoPageId = 'torontoRoseDelivery';
const torontoRedRosesDeliverySeoPageId = 'torontoRedRosesDelivery';
const torontoWhiteRosesDeliverySeoPageId = 'torontoWhiteRosesDelivery';
const torontoPinkRosesDeliverySeoPageId = 'torontoPinkRosesDelivery';
const torontoHundredRosesDeliverySeoPageId = 'torontoHundredRosesDelivery';
const torontoCheapRosesDeliverySeoPageId = 'torontoCheapRosesDelivery';
const torontoTwelveRosesDeliverySeoPageId = 'torontoTwelveRosesDelivery';
const roseDeliveryHubSeoPageId = 'roseDeliveryHub';
const mississaugaRoseDeliverySeoPageId = 'mississaugaRoseDelivery';
const vaughanRoseDeliverySeoPageId = 'vaughanRoseDelivery';
const markhamRoseDeliverySeoPageId = 'markhamRoseDelivery';
const richmondHillRoseDeliverySeoPageId = 'richmondHillRoseDelivery';
const bramptonRoseDeliverySeoPageId = 'bramptonRoseDelivery';
const burlingtonRoseDeliverySeoPageId = 'burlingtonRoseDelivery';
const northYorkRoseDeliverySeoPageId = 'northYorkRoseDelivery';
const scarboroughRoseDeliverySeoPageId = 'scarboroughRoseDelivery';
const oakvilleRoseDeliverySeoPageId = 'oakvilleRoseDelivery';
const etobicokeRoseDeliverySeoPageId = 'etobicokeRoseDelivery';
const nationalFlowerDayCanadaBlogArticlePageId = 'nationalFlowerDayCanada';
const mostPopularFlowersCanadaBlogArticlePageId = 'mostPopularFlowersCanada';
const indoorBloomingPlantsBlogArticlePageId = 'indoorBloomingPlants';
const howToAskValentineBlogArticlePageId = 'howToAskValentine';
const valentinesDayCanadaBlogArticlePageId = 'valentinesDayCanada';
const mothersDayCanadaBlogArticlePageId = 'mothersDayCanada';
const bridalShowerIdeasTorontoBlogArticlePageId = 'bridalShowerIdeasToronto';
const howLongDoHeliumBalloonsLastBlogArticlePageId = 'howLongDoHeliumBalloonsLast';
const howToDeflateFoilBalloonsBlogArticlePageId = 'howToDeflateFoilBalloons';
const flowerMeaningsHubBlogArticlePageId = 'flowerMeaningsHub';
const birthFlowersHubBlogArticlePageId = 'birthFlowersHub';
const meaningOfRedRosesBlogArticlePageId = 'meaningOfRedRoses';
const meaningOfYellowRosesBlogArticlePageId = 'meaningOfYellowRoses';
const meaningOfSnowdropsBlogArticlePageId = 'meaningOfSnowdrops';
const meaningOfCarnationsBlogArticlePageId = 'meaningOfCarnations';
const meaningOfPrimrosesBlogArticlePageId = 'meaningOfPrimroses';
const meaningOfVioletsBlogArticlePageId = 'meaningOfViolets';
const meaningOfDaffodilBlogArticlePageId = 'meaningOfDaffodil';
const meaningOfSweetPeaBlogArticlePageId = 'meaningOfSweetPea';
const meaningOfDaisyBlogArticlePageId = 'meaningOfDaisy';
const meaningOfHawthornBlogArticlePageId = 'meaningOfHawthorn';
const meaningOfLilyOfTheValleyBlogArticlePageId = 'meaningOfLilyOfTheValley';
const meaningOfWhiteLiliesBlogArticlePageId = 'meaningOfWhiteLilies';
const meaningOfPinkLiliesBlogArticlePageId = 'meaningOfPinkLilies';
const meaningOfYellowLiliesBlogArticlePageId = 'meaningOfYellowLilies';
const meaningOfOrangeLiliesBlogArticlePageId = 'meaningOfOrangeLilies';
const meaningOfRedLiliesBlogArticlePageId = 'meaningOfRedLilies';
const meaningOfPurpleLiliesBlogArticlePageId = 'meaningOfPurpleLilies';
const meaningOfBlackLiliesBlogArticlePageId = 'meaningOfBlackLilies';
const meaningOfCallaLiliesBlogArticlePageId = 'meaningOfCallaLilies';
const meaningOfStargazerLiliesBlogArticlePageId = 'meaningOfStargazerLilies';
const meaningOfTigerLiliesBlogArticlePageId = 'meaningOfTigerLilies';
const meaningOfEasterLiliesBlogArticlePageId = 'meaningOfEasterLilies';
const meaningOfOrchidsBlogArticlePageId = 'meaningOfOrchids';
const meaningOfHydrangeasBlogArticlePageId = 'meaningOfHydrangeas';
const meaningOfHoneysuckleBlogArticlePageId = 'meaningOfHoneysuckle';
const meaningOfRoseBlogArticlePageId = 'meaningOfRose';
const meaningOfWaterLilyBlogArticlePageId = 'meaningOfWaterLily';
const meaningOfLarkspurBlogArticlePageId = 'meaningOfLarkspur';
const meaningOfPoppyBlogArticlePageId = 'meaningOfPoppy';
const meaningOfGladiolusBlogArticlePageId = 'meaningOfGladiolus';
const meaningOfMorningGloryBlogArticlePageId = 'meaningOfMorningGlory';
const meaningOfAsterBlogArticlePageId = 'meaningOfAster';
const meaningOfCosmosBlogArticlePageId = 'meaningOfCosmos';
const meaningOfMarigoldBlogArticlePageId = 'meaningOfMarigold';
const meaningOfPeonyBlogArticlePageId = 'meaningOfPeony';
const meaningOfChrysanthemumBlogArticlePageId = 'meaningOfChrysanthemum';
const meaningOfHollyBlogArticlePageId = 'meaningOfHolly';
const meaningOfNarcissusBlogArticlePageId = 'meaningOfNarcissus';
const topFlowerShopsToronto2026BlogArticlePageId = 'topFlowerShopsToronto2026';
const januaryBirthFlowerBlogArticlePageId = 'januaryBirthFlower';
const februaryBirthFlowerBlogArticlePageId = 'februaryBirthFlower';
const marchBirthFlowerBlogArticlePageId = 'marchBirthFlower';
const aprilBirthFlowerBlogArticlePageId = 'aprilBirthFlower';
const mayBirthFlowerBlogArticlePageId = 'mayBirthFlower';
const juneBirthFlowerBlogArticlePageId = 'juneBirthFlower';
const julyBirthFlowerBlogArticlePageId = 'julyBirthFlower';
const augustBirthFlowerBlogArticlePageId = 'augustBirthFlower';
const septemberBirthFlowerBlogArticlePageId = 'septemberBirthFlower';
const octoberBirthFlowerBlogArticlePageId = 'octoberBirthFlower';
const novemberBirthFlowerBlogArticlePageId = 'novemberBirthFlower';
const decemberBirthFlowerBlogArticlePageId = 'decemberBirthFlower';
const sympathyCardMessagesBlogArticlePageId = 'sympathyCardMessages';
const howToKeepCutFlowersFreshBlogArticlePageId = 'howToKeepCutFlowersFresh';
const howToMakeFlowersLastLongerBlogArticlePageId = 'howToMakeFlowersLastLonger';
const howLongDoCutFlowersLastBlogArticlePageId = 'howLongDoCutFlowersLast';
const whatToPutInFlowerWaterBlogArticlePageId = 'whatToPutInFlowerWater';
const apologyFlowersBlogArticlePageId = 'apologyFlowers';
const howToCareForOrchidsBlogArticlePageId = 'howToCareForOrchids';
const howToCareForTulipsBlogArticlePageId = 'howToCareForTulips';
const howToCareForPeoniesBlogArticlePageId = 'howToCareForPeonies';
const howToCareForHydrangeasBlogArticlePageId = 'howToCareForHydrangeas';
const howToCareForRosesBlogArticlePageId = 'howToCareForRoses';
const howMuchDoFlowersCostBlogArticlePageId = 'howMuchDoFlowersCost';
const howToChooseAFloristBlogArticlePageId = 'howToChooseAFlorist';
const bestFlowersForAFirstDateBlogArticlePageId = 'bestFlowersForAFirstDate';
const weddingFlowersOnABudgetBlogArticlePageId = 'weddingFlowersOnABudget';
const areFlowerSubscriptionsWorthItBlogArticlePageId = 'areFlowerSubscriptionsWorthIt';
const howToCareForSunflowersBlogArticlePageId = 'howToCareForSunflowers';
const howToCareForLiliesBlogArticlePageId = 'howToCareForLilies';
const howToCareForCarnationsBlogArticlePageId = 'howToCareForCarnations';
const howToCareForGerberaDaisiesBlogArticlePageId = 'howToCareForGerberaDaisies';
const howToCareForRanunculusBlogArticlePageId = 'howToCareForRanunculus';
const bestFlowersForMothersDayBlogArticlePageId = 'bestFlowersForMothersDay';
const fathersDayFlowersCanadaBlogArticlePageId = 'fathersDayFlowersCanada';
const thanksgivingFlowersCanadaBlogArticlePageId = 'thanksgivingFlowersCanada';
const remembranceDayFlowersBlogArticlePageId = 'remembranceDayFlowers';
const canadaDayFlowersBlogArticlePageId = 'canadaDayFlowers';
const lunarNewYearFlowersBlogArticlePageId = 'lunarNewYearFlowers';
const diwaliFlowersBlogArticlePageId = 'diwaliFlowers';
const howToCareForFreshFlowersBlogArticlePageId = 'howToCareForFreshFlowers';
const flowersForAnniversaryBlogArticlePageId = 'flowersForAnniversary';
const flowersForBirthdayBlogArticlePageId = 'flowersForBirthday';
const flowersForGetWellBlogArticlePageId = 'flowersForGetWell';
const flowersForNewBabyBlogArticlePageId = 'flowersForNewBaby';
const flowersForHousewarmingBlogArticlePageId = 'flowersForHousewarming';
const springFlowersBlogArticlePageId = 'springFlowers';
const summerFlowersBlogArticlePageId = 'summerFlowers';
const fallFlowersBlogArticlePageId = 'fallFlowers';
const winterFlowersBlogArticlePageId = 'winterFlowers';
const typesOfFlowersBlogArticlePageId = 'typesOfFlowers';
const redFlowersBlogArticlePageId = 'redFlowers';
const pinkFlowersBlogArticlePageId = 'pinkFlowers';
const yellowFlowersBlogArticlePageId = 'yellowFlowers';
const whiteFlowersBlogArticlePageId = 'whiteFlowers';
const purpleFlowersBlogArticlePageId = 'purpleFlowers';
const christmasFlowersBlogArticlePageId = 'christmasFlowers';
const easterFlowersBlogArticlePageId = 'easterFlowers';
const typesOfRosesBlogArticlePageId = 'typesOfRoses';
const bestSmellingFlowersBlogArticlePageId = 'bestSmellingFlowers';
const flowersForFuneralBlogArticlePageId = 'flowersForFuneral';
const blueFlowersBlogArticlePageId = 'blueFlowers';
const orangeFlowersBlogArticlePageId = 'orangeFlowers';
const tropicalFlowersBlogArticlePageId = 'tropicalFlowers';
const exoticFlowersBlogArticlePageId = 'exoticFlowers';
const typesOfTulipsBlogArticlePageId = 'typesOfTulips';
const greenFlowersBlogArticlePageId = 'greenFlowers';
const blackFlowersBlogArticlePageId = 'blackFlowers';
const driedFlowersBlogArticlePageId = 'driedFlowers';
const foreverRosesBlogArticlePageId = 'foreverRoses';
const preservedFlowersBlogArticlePageId = 'preservedFlowers';
const rosePetalsBlogArticlePageId = 'rosePetals';
const artificialFlowersBlogArticlePageId = 'artificialFlowers';
const flowersForGraduationBlogArticlePageId = 'flowersForGraduation';
const typesOfPeoniesBlogArticlePageId = 'typesOfPeonies';
const flowersThatLookLikeRosesBlogArticlePageId = 'flowersThatLookLikeRoses';
const meaningOfWhiteRosesBlogArticlePageId = 'meaningOfWhiteRoses';
const meaningOfPinkRosesBlogArticlePageId = 'meaningOfPinkRoses';
const roseColorMeaningBlogArticlePageId = 'roseColorMeaning';
const numberOfRosesMeaningBlogArticlePageId = 'numberOfRosesMeaning';
const typesOfSunflowersBlogArticlePageId = 'typesOfSunflowers';
const howToDryFlowersBlogArticlePageId = 'howToDryFlowers';
const wildflowerBouquetBlogArticlePageId = 'wildflowerBouquet';
const howToDryRosesBlogArticlePageId = 'howToDryRoses';
const howToWrapFlowersBlogArticlePageId = 'howToWrapFlowers';
const flowerPreservationBlogArticlePageId = 'flowerPreservation';
const howToPressFlowersBlogArticlePageId = 'howToPressFlowers';
const flowersToxicToCatsBlogArticlePageId = 'flowersToxicToCats';
const nowruzFlowersBlogArticlePageId = 'nowruzFlowers';
const peonySeasonCanadaBlogArticlePageId = 'peonySeasonCanada';
const promFlowersBlogArticlePageId = 'promFlowers';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// Legacy Sharetribe routes (login/signup/inbox/profile/listing editor/account
// settings). The pages behind them were removed together with the Flex
// backend; the route *names* are kept because shared components still build
// links with them, and old bookmarks / emails still point at the paths. Each
// one now lands on the equivalent Firebase-era page.
const RedirectToShopPortal = () => <NamedRedirect name="ShopPortalPage" />;
const RedirectToCustomerAccount = () => <NamedRedirect name="CustomerAccountPage" />;
const RedirectToCustomerMessages = () => (
  <NamedRedirect name="CustomerAccountPage" search="?tab=messages" />
);
const RedirectToCart = () => <NamedRedirect name="CartPage" />;
// On this build listing authors are shops, so the author id is the store id
// (UserCard links the same way).
const RedirectProfileToStore = props =>
  props.params?.id ? (
    <NamedRedirect name="CatalogShopPage" params={{ id: props.params.id }} />
  ) : (
    <NamedRedirect name="LandingPage" />
  );
const RedirectInbox = props =>
  props.params?.tab === 'sales' ? <RedirectToShopPortal /> : <RedirectToCustomerMessages />;

const createPermanentRedirect = name => props => (
  <NamedRedirect name={name} permanent staticContext={props.staticContext} />
);
const RedirectToLandingPagePermanently = createPermanentRedirect('LandingPage');
const RedirectToPortCreditFlowerDeliveryPage = createPermanentRedirect('PortCreditFlowerDeliveryPage');
const RedirectToMississaugaFlowerDeliveryPage = createPermanentRedirect('MississaugaFlowerDeliveryPage');
const RedirectToTorontoFlowerDeliveryPage = createPermanentRedirect('TorontoFlowerDeliveryPage');
const RedirectToVaughanFlowerDeliveryPage = createPermanentRedirect('VaughanFlowerDeliveryPage');
const RedirectToMarkhamFlowerDeliveryPage = createPermanentRedirect('MarkhamFlowerDeliveryPage');
const RedirectToRichmondHillFlowerDeliveryPage = createPermanentRedirect('RichmondHillFlowerDeliveryPage');
const RedirectToBramptonFlowerDeliveryPage = createPermanentRedirect('BramptonFlowerDeliveryPage');
const RedirectToBurlingtonFlowerDeliveryPage = createPermanentRedirect('BurlingtonFlowerDeliveryPage');
const RedirectToNorthYorkFlowerDeliveryPage = createPermanentRedirect('NorthYorkFlowerDeliveryPage');
const RedirectToScarboroughFlowerDeliveryPage = createPermanentRedirect('ScarboroughFlowerDeliveryPage');
const RedirectToOakvilleFlowerDeliveryPage = createPermanentRedirect('OakvilleFlowerDeliveryPage');
const RedirectToEtobicokeFlowerDeliveryPage = createPermanentRedirect('EtobicokeFlowerDeliveryPage');
const RedirectToTorontoBirthdayFlowersPage = createPermanentRedirect('TorontoBirthdayFlowersPage');
const RedirectToTorontoAnniversaryFlowersPage = createPermanentRedirect('TorontoAnniversaryFlowersPage');
const RedirectToTorontoGetWellFlowersPage = createPermanentRedirect('TorontoGetWellFlowersPage');
const RedirectToTorontoThankYouFlowersPage = createPermanentRedirect('TorontoThankYouFlowersPage');
const RedirectToTorontoWeddingFlowersPage = createPermanentRedirect('TorontoWeddingFlowersPage');
const RedirectToMississaugaWeddingFlowersPage = createPermanentRedirect('MississaugaWeddingFlowersPage');
const RedirectToTorontoCakesDeliveryPage = createPermanentRedirect('TorontoCakesDeliveryPage');
const RedirectToTorontoBentoCakesDeliveryPage = createPermanentRedirect('TorontoBentoCakesDeliveryPage');
const RedirectToTorontoChocolateCoveredStrawberriesDeliveryPage = createPermanentRedirect(
  'TorontoChocolateCoveredStrawberriesDeliveryPage'
);
const RedirectToTorontoBalloonArchDecorPage = createPermanentRedirect('TorontoBalloonArchDecorPage');
const RedirectToTorontoBalloonGarlandDecorPage = createPermanentRedirect(
  'TorontoBalloonGarlandDecorPage'
);
const RedirectToTorontoBirthdayBalloonDecorPage = createPermanentRedirect(
  'TorontoBirthdayBalloonDecorPage'
);
const RedirectToTorontoBabyShowerDecorPage = createPermanentRedirect('TorontoBabyShowerDecorPage');
const RedirectToTorontoBabyBoyShowerDecorPage = createPermanentRedirect(
  'TorontoBabyBoyShowerDecorPage'
);
const RedirectToTorontoBabyGirlShowerDecorPage = createPermanentRedirect(
  'TorontoBabyGirlShowerDecorPage'
);
const RedirectToTorontoBridalShowerDecorPage = createPermanentRedirect(
  'TorontoBridalShowerDecorPage'
);
const RedirectToRoseDeliveryHubPage = createPermanentRedirect('RoseDeliveryHubPage');
const RedirectToTorontoRoseDeliveryPage = createPermanentRedirect('TorontoRoseDeliveryPage');
const RedirectToMississaugaRoseDeliveryPage = createPermanentRedirect('MississaugaRoseDeliveryPage');
const RedirectToVaughanRoseDeliveryPage = createPermanentRedirect('VaughanRoseDeliveryPage');
const RedirectToMarkhamRoseDeliveryPage = createPermanentRedirect('MarkhamRoseDeliveryPage');
const RedirectToRichmondHillRoseDeliveryPage = createPermanentRedirect(
  'RichmondHillRoseDeliveryPage'
);
const RedirectToBramptonRoseDeliveryPage = createPermanentRedirect('BramptonRoseDeliveryPage');
const RedirectToBurlingtonRoseDeliveryPage = createPermanentRedirect('BurlingtonRoseDeliveryPage');
const RedirectToNorthYorkRoseDeliveryPage = createPermanentRedirect('NorthYorkRoseDeliveryPage');
const RedirectToScarboroughRoseDeliveryPage = createPermanentRedirect('ScarboroughRoseDeliveryPage');
const RedirectToOakvilleRoseDeliveryPage = createPermanentRedirect('OakvilleRoseDeliveryPage');
const RedirectToEtobicokeRoseDeliveryPage = createPermanentRedirect('EtobicokeRoseDeliveryPage');
const RedirectToTorontoRedRosesDeliveryPage = createPermanentRedirect('TorontoRedRosesDeliveryPage');
const RedirectToBlogHubPage = createPermanentRedirect('BlogHubPage');
const RedirectToNationalFlowerDayCanadaBlogArticlePage = createPermanentRedirect(
  'NationalFlowerDayCanadaBlogArticlePage'
);
const RedirectToMostPopularFlowersCanadaBlogArticlePage = createPermanentRedirect(
  'MostPopularFlowersCanadaBlogArticlePage'
);
const RedirectToIndoorBloomingPlantsBlogArticlePage = createPermanentRedirect(
  'IndoorBloomingPlantsBlogArticlePage'
);
const RedirectToHowToAskValentineBlogArticlePage = createPermanentRedirect(
  'HowToAskValentineBlogArticlePage'
);
const RedirectToValentinesDayCanadaBlogArticlePage = createPermanentRedirect(
  'ValentinesDayCanadaBlogArticlePage'
);
const RedirectToMothersDayCanadaBlogArticlePage = createPermanentRedirect(
  'MothersDayCanadaBlogArticlePage'
);
const RedirectToHowLongDoHeliumBalloonsLastBlogArticlePage = createPermanentRedirect(
  'HowLongDoHeliumBalloonsLastBlogArticlePage'
);
const RedirectToHowToDeflateFoilBalloonsBlogArticlePage = createPermanentRedirect(
  'HowToDeflateFoilBalloonsBlogArticlePage'
);
const RedirectToFlowerMeaningsHubBlogArticlePage = createPermanentRedirect(
  'FlowerMeaningsHubBlogArticlePage'
);
const RedirectToBirthFlowersHubBlogArticlePage = createPermanentRedirect(
  'BirthFlowersHubBlogArticlePage'
);
const RedirectToMeaningOfRedRosesBlogArticlePage = createPermanentRedirect(
  'MeaningOfRedRosesBlogArticlePage'
);
const RedirectToMeaningOfYellowRosesBlogArticlePage = createPermanentRedirect(
  'MeaningOfYellowRosesBlogArticlePage'
);
const RedirectToMeaningOfSnowdropsBlogArticlePage = createPermanentRedirect(
  'MeaningOfSnowdropsBlogArticlePage'
);
const RedirectToMeaningOfCarnationsBlogArticlePage = createPermanentRedirect(
  'MeaningOfCarnationsBlogArticlePage'
);
const RedirectToMeaningOfPrimrosesBlogArticlePage = createPermanentRedirect(
  'MeaningOfPrimrosesBlogArticlePage'
);
const RedirectToMeaningOfVioletsBlogArticlePage = createPermanentRedirect(
  'MeaningOfVioletsBlogArticlePage'
);
const RedirectToMeaningOfDaffodilBlogArticlePage = createPermanentRedirect(
  'MeaningOfDaffodilBlogArticlePage'
);
const RedirectToMeaningOfSweetPeaBlogArticlePage = createPermanentRedirect(
  'MeaningOfSweetPeaBlogArticlePage'
);
const RedirectToMeaningOfDaisyBlogArticlePage = createPermanentRedirect(
  'MeaningOfDaisyBlogArticlePage'
);
const RedirectToMeaningOfHawthornBlogArticlePage = createPermanentRedirect(
  'MeaningOfHawthornBlogArticlePage'
);
const RedirectToMeaningOfLilyOfTheValleyBlogArticlePage = createPermanentRedirect(
  'MeaningOfLilyOfTheValleyBlogArticlePage'
);
const RedirectToMeaningOfWhiteLiliesBlogArticlePage = createPermanentRedirect(
  'MeaningOfWhiteLiliesBlogArticlePage'
);
const RedirectToMeaningOfHoneysuckleBlogArticlePage = createPermanentRedirect(
  'MeaningOfHoneysuckleBlogArticlePage'
);
const RedirectToMeaningOfRoseBlogArticlePage = createPermanentRedirect(
  'MeaningOfRoseBlogArticlePage'
);
const RedirectToMeaningOfWaterLilyBlogArticlePage = createPermanentRedirect(
  'MeaningOfWaterLilyBlogArticlePage'
);
const RedirectToMeaningOfLarkspurBlogArticlePage = createPermanentRedirect(
  'MeaningOfLarkspurBlogArticlePage'
);
const RedirectToMeaningOfPoppyBlogArticlePage = createPermanentRedirect(
  'MeaningOfPoppyBlogArticlePage'
);
const RedirectToMeaningOfGladiolusBlogArticlePage = createPermanentRedirect(
  'MeaningOfGladiolusBlogArticlePage'
);
const RedirectToMeaningOfMorningGloryBlogArticlePage = createPermanentRedirect(
  'MeaningOfMorningGloryBlogArticlePage'
);
const RedirectToMeaningOfAsterBlogArticlePage = createPermanentRedirect(
  'MeaningOfAsterBlogArticlePage'
);
const RedirectToMeaningOfCosmosBlogArticlePage = createPermanentRedirect(
  'MeaningOfCosmosBlogArticlePage'
);
const RedirectToMeaningOfMarigoldBlogArticlePage = createPermanentRedirect(
  'MeaningOfMarigoldBlogArticlePage'
);
const RedirectToMeaningOfPeonyBlogArticlePage = createPermanentRedirect(
  'MeaningOfPeonyBlogArticlePage'
);
const RedirectToMeaningOfChrysanthemumBlogArticlePage = createPermanentRedirect(
  'MeaningOfChrysanthemumBlogArticlePage'
);
const RedirectToMeaningOfHollyBlogArticlePage = createPermanentRedirect(
  'MeaningOfHollyBlogArticlePage'
);
const RedirectToMeaningOfNarcissusBlogArticlePage = createPermanentRedirect(
  'MeaningOfNarcissusBlogArticlePage'
);
const RedirectToTopFlowerShopsToronto2026BlogArticlePage = createPermanentRedirect(
  'TopFlowerShopsToronto2026BlogArticlePage'
);
const RedirectToJanuaryBirthFlowerBlogArticlePage = createPermanentRedirect(
  'JanuaryBirthFlowerBlogArticlePage'
);
const RedirectToFebruaryBirthFlowerBlogArticlePage = createPermanentRedirect(
  'FebruaryBirthFlowerBlogArticlePage'
);
const RedirectToMarchBirthFlowerBlogArticlePage = createPermanentRedirect(
  'MarchBirthFlowerBlogArticlePage'
);
const RedirectToAprilBirthFlowerBlogArticlePage = createPermanentRedirect(
  'AprilBirthFlowerBlogArticlePage'
);
const RedirectToMayBirthFlowerBlogArticlePage = createPermanentRedirect(
  'MayBirthFlowerBlogArticlePage'
);
const RedirectToJuneBirthFlowerBlogArticlePage = createPermanentRedirect(
  'JuneBirthFlowerBlogArticlePage'
);
const RedirectToJulyBirthFlowerBlogArticlePage = createPermanentRedirect(
  'JulyBirthFlowerBlogArticlePage'
);
const RedirectToAugustBirthFlowerBlogArticlePage = createPermanentRedirect(
  'AugustBirthFlowerBlogArticlePage'
);
const RedirectToSeptemberBirthFlowerBlogArticlePage = createPermanentRedirect(
  'SeptemberBirthFlowerBlogArticlePage'
);
const RedirectToOctoberBirthFlowerBlogArticlePage = createPermanentRedirect(
  'OctoberBirthFlowerBlogArticlePage'
);
const RedirectToNovemberBirthFlowerBlogArticlePage = createPermanentRedirect(
  'NovemberBirthFlowerBlogArticlePage'
);
const RedirectToDecemberBirthFlowerBlogArticlePage = createPermanentRedirect(
  'DecemberBirthFlowerBlogArticlePage'
);
const seoLocationPageLoadData = seoPageId => (params, search, config) =>
  pageDataLoadingAPI.SeoLocationPage.loadData({ ...params, seoPageId }, search, config);

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig, accessControlConfig) => {
  const SearchPage =
    layoutConfig.searchPage?.variantType === 'map'
      ? SearchPageWithMap
      : SearchPageWithGrid;
  const ListingPage =
    layoutConfig.listingPage?.variantType === 'carousel'
      ? ListingPageCarousel
      : ListingPageCoverPhoto;

  const isPrivateMarketplace =
    accessControlConfig?.marketplace?.private === true;
  const authForPrivateMarketplace = isPrivateMarketplace ? { auth: true } : {};

  return [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/home-preview',
      name: 'HomePreviewPage',
      component: HomePreviewPage,
      loadData: seoLocationPageLoadData(homePreviewSeoPageId),
      extraProps: { seoPageId: homePreviewSeoPageId },
    },
    {
      path: '/blog',
      name: 'BlogHubPage',
      component: BlogHubPage,
    },
    {
      path: '/browse',
      name: 'CatalogBrowsePage',
      component: CatalogPage,
      extraProps: { mode: 'browse' },
    },
    {
      path: '/store/:id',
      name: 'CatalogShopPage',
      component: CatalogPage,
      extraProps: { mode: 'shop' },
    },
    {
      // Old catalog product URLs → the real listing page.
      path: '/item/:id',
      name: 'CatalogProductPage',
      component: props => (
        <NamedRedirect
          name="ListingPage"
          params={{ id: props.params?.id, slug: 'item' }}
          staticContext={props.staticContext}
        />
      ),
    },
    {
      path: '/admin',
      name: 'CatalogAdminPage',
      component: CatalogAdminPage,
    },
    {
      path: '/admin/inquiries',
      name: 'CatalogAdminInquiriesPage',
      component: CatalogAdminPage,
    },
    {
      path: '/my-shop',
      name: 'ShopPortalPage',
      component: ShopPortalPage,
    },
    {
      path: '/account',
      name: 'CustomerAccountPage',
      component: CustomerAccountPage,
    },
    {
      path: '/wishes',
      name: 'WishesHubPage',
      component: WishesPage,
    },
    {
      path: '/quotes',
      name: 'QuotesHubPage',
      component: WishesPage,
      extraProps: { hub: 'quotes' },
    },
    {
      path: '/quotes/friendship-quotes',
      name: 'FriendshipQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'friendship-quotes' },
    },
    {
      path: '/quotes/happy-birthday-quotes',
      name: 'HappyBirthdayQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'happy-birthday-quotes' },
    },
    {
      path: '/quotes/flower-quotes',
      name: 'FlowerQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'flower-quotes' },
    },
    {
      path: '/quotes/mother-quotes',
      name: 'MotherQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'mother-quotes' },
    },
    {
      path: '/quotes/mother-daughter-quotes',
      name: 'MotherDaughterQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'mother-daughter-quotes' },
    },
    {
      path: '/quotes/mother-and-son-quotes',
      name: 'MotherAndSonQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'mother-and-son-quotes' },
    },
    {
      path: '/quotes/christian-birthday-wishes',
      name: 'ChristianBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'christian-birthday-wishes' },
    },
    {
      path: '/quotes/bible-verses-for-birthday',
      name: 'BibleVersesForBirthdayPage',
      component: WishesPage,
      extraProps: { categoryId: 'bible-verses-for-birthday' },
    },
    {
      path: '/quotes/happy-heavenly-birthday-quotes',
      name: 'HappyHeavenlyBirthdayQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'happy-heavenly-birthday-quotes' },
    },
    {
      path: '/quotes/thinking-of-you-quotes',
      name: 'ThinkingOfYouQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'thinking-of-you-quotes' },
    },
    {
      path: '/fr/wishes/quoi-ecrire-fleurs-funerailles',
      name: 'QuoiEcrireFleursFuneraillesPage',
      component: WishesPage,
      extraProps: { categoryId: 'quoi-ecrire-fleurs-funerailles' },
    },
    {
      path: '/fr/wishes/remerciements-depart-retraite',
      name: 'RemerciementsDepartRetraitePage',
      component: WishesPage,
      extraProps: { categoryId: 'remerciements-depart-retraite' },
    },
    {
      path: '/fr/wishes/quoi-ecrire-carte-diplome',
      name: 'QuoiEcrireCarteDiplomePage',
      component: WishesPage,
      extraProps: { categoryId: 'quoi-ecrire-carte-diplome' },
    },
    {
      path: '/fr/wishes/remerciements-voeux-anniversaire',
      name: 'RemerciementsVoeuxAnniversairePage',
      component: WishesPage,
      extraProps: { categoryId: 'remerciements-voeux-anniversaire' },
    },
    {
      path: '/fr/wishes/bon-retablissement-drole',
      name: 'BonRetablissementDrolePage',
      component: WishesPage,
      extraProps: { categoryId: 'bon-retablissement-drole' },
    },
    {
      path: '/fr/wishes/felicitations-examen-reussi',
      name: 'FelicitationsExamenReussiPage',
      component: WishesPage,
      extraProps: { categoryId: 'felicitations-examen-reussi' },
    },
    {
      path: '/fr/wishes/texte-retraite-drole',
      name: 'TexteRetraiteDrolePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-retraite-drole' },
    },
    {
      path: '/fr/wishes/texte-mariage-drole',
      name: 'TexteMariageDrolePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-mariage-drole' },
    },
    {
      path: '/fr/wishes/retraite-patron',
      name: 'RetraitePatronPage',
      component: WishesPage,
      extraProps: { categoryId: 'retraite-patron' },
    },
    {
      path: '/fr/wishes/retraite-parents',
      name: 'RetraiteParentsPage',
      component: WishesPage,
      extraProps: { categoryId: 'retraite-parents' },
    },
    {
      path: '/fr/wishes/remerciement-enseignant',
      name: 'RemerciementEnseignantPage',
      component: WishesPage,
      extraProps: { categoryId: 'remerciement-enseignant' },
    },
    {
      path: '/fr/wishes/messages-baby-shower',
      name: 'MessagesBabyShowerPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-baby-shower' },
    },
    {
      path: '/fr/wishes/mariage-soeur',
      name: 'MariageSoeurPage',
      component: WishesPage,
      extraProps: { categoryId: 'mariage-soeur' },
    },
    {
      path: '/fr/wishes/mariage-frere',
      name: 'MariageFrerePage',
      component: WishesPage,
      extraProps: { categoryId: 'mariage-frere' },
    },
    {
      path: '/fr/wishes/mariage-fille',
      name: 'MariageFillePage',
      component: WishesPage,
      extraProps: { categoryId: 'mariage-fille' },
    },
    {
      path: '/fr/wishes/felicitations-doctorat',
      name: 'FelicitationsDoctoratPage',
      component: WishesPage,
      extraProps: { categoryId: 'felicitations-doctorat' },
    },
    {
      path: '/fr/wishes/felicitations-diplome-soeur',
      name: 'FelicitationsDiplomeSoeurPage',
      component: WishesPage,
      extraProps: { categoryId: 'felicitations-diplome-soeur' },
    },
    {
      path: '/fr/wishes/felicitations-diplome-petite-fille',
      name: 'FelicitationsDiplomePetiteFillePage',
      component: WishesPage,
      extraProps: { categoryId: 'felicitations-diplome-petite-fille' },
    },
    {
      path: '/fr/wishes/felicitations-diplome-neveu-niece',
      name: 'FelicitationsDiplomeNeveuNiecePage',
      component: WishesPage,
      extraProps: { categoryId: 'felicitations-diplome-neveu-niece' },
    },
    {
      path: '/fr/wishes/felicitations-diplome-fils',
      name: 'FelicitationsDiplomeFilsPage',
      component: WishesPage,
      extraProps: { categoryId: 'felicitations-diplome-fils' },
    },
    {
      path: '/fr/wishes/felicitations-diplome-fille',
      name: 'FelicitationsDiplomeFillePage',
      component: WishesPage,
      extraProps: { categoryId: 'felicitations-diplome-fille' },
    },
    {
      path: '/fr/wishes/anniversaire-d-embauche',
      name: 'AnniversaireDEmbauchePage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversaire-d-embauche' },
    },
    {
      path: '/fr/wishes/retraite-enseignant',
      name: 'RetraiteEnseignantPage',
      component: WishesPage,
      extraProps: { categoryId: 'retraite-enseignant' },
    },
    {
      path: '/fr/wishes/retraite-ami',
      name: 'RetraiteAmiPage',
      component: WishesPage,
      extraProps: { categoryId: 'retraite-ami' },
    },
    {
      path: '/fr/wishes/messages-halloween',
      name: 'MessagesHalloweenPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-halloween' },
    },
    {
      path: '/fr/wishes/joyeuses-paques',
      name: 'JoyeusesPaquesPage',
      component: WishesPage,
      extraProps: { categoryId: 'joyeuses-paques' },
    },
    {
      path: '/fr/wishes/versets-bibliques-anniversaire',
      name: 'VersetsBibliquesAnniversairePage',
      component: WishesPage,
      extraProps: { categoryId: 'versets-bibliques-anniversaire' },
    },
    {
      path: '/fr/wishes/citations-action-de-grace',
      name: 'CitationsActionDeGracePage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-action-de-grace' },
    },
    {
      path: '/fr/wishes/citations-je-pense-a-toi',
      name: 'CitationsJePenseAToiPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-je-pense-a-toi' },
    },
    {
      path: '/fr/wishes/citations-remerciement',
      name: 'CitationsRemerciementPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-remerciement' },
    },
    {
      path: '/fr/wishes/citations-mere-fille',
      name: 'CitationsMereFillePage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-mere-fille' },
    },
    {
      path: '/fr/wishes/message-depart-collegue',
      name: 'MessageDepartColleguePage',
      component: WishesPage,
      extraProps: { categoryId: 'message-depart-collegue' },
    },
    {
      path: '/fr/wishes/benedictions-du-matin',
      name: 'BenedictionsDuMatinPage',
      component: WishesPage,
      extraProps: { categoryId: 'benedictions-du-matin' },
    },
    {
      path: '/fr/wishes/priere-du-matin',
      name: 'PriereDuMatinPage',
      component: WishesPage,
      extraProps: { categoryId: 'priere-du-matin' },
    },
    {
      path: '/fr/wishes/message-depart-drole',
      name: 'MessageDepartDrolePage',
      component: WishesPage,
      extraProps: { categoryId: 'message-depart-drole' },
    },
    {
      path: '/fr/wishes/message-depart-equipe',
      name: 'MessageDepartEquipePage',
      component: WishesPage,
      extraProps: { categoryId: 'message-depart-equipe' },
    },
    {
      path: '/fr/wishes/message-depart-patron',
      name: 'MessageDepartPatronPage',
      component: WishesPage,
      extraProps: { categoryId: 'message-depart-patron' },
    },
    {
      path: '/fr/wishes/jour-du-souvenir',
      name: 'JourDuSouvenirPage',
      component: WishesPage,
      extraProps: { categoryId: 'jour-du-souvenir' },
    },
    {
      path: '/fr/wishes/bonne-fete-du-canada',
      name: 'BonneFeteDuCanadaPage',
      component: WishesPage,
      extraProps: { categoryId: 'bonne-fete-du-canada' },
    },
    {
      path: '/fr/wishes/citations-anniversaire-de-mariage',
      name: 'CitationsAnniversaireDeMariagePage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-anniversaire-de-mariage' },
    },
    {
      path: '/fr/wishes/citations-mere-fils',
      name: 'CitationsMereFilsPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-mere-fils' },
    },
    {
      path: '/fr/wishes/citations-grand-mere',
      name: 'CitationsGrandMerePage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-grand-mere' },
    },
    {
      path: '/fr/wishes/citations-saint-valentin',
      name: 'CitationsSaintValentinPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-saint-valentin' },
    },
    {
      path: '/fr/wishes/ramadan-moubarak',
      name: 'RamadanMoubarakPage',
      component: WishesPage,
      extraProps: { categoryId: 'ramadan-moubarak' },
    },
    {
      path: '/fr/wishes/quoi-ecrire-carte-anniversaire-de-mariage',
      name: 'QuoiEcrireCarteAnniversaireDeMariagePage',
      component: WishesPage,
      extraProps: { categoryId: 'quoi-ecrire-carte-anniversaire-de-mariage' },
    },
    {
      path: '/fr/wishes/messages-action-de-grace',
      name: 'MessagesActionDeGracePage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-action-de-grace' },
    },
    {
      path: '/fr/wishes/joyeux-diwali',
      name: 'JoyeuxDiwaliPage',
      component: WishesPage,
      extraProps: { categoryId: 'joyeux-diwali' },
    },
    {
      path: '/fr/wishes/aid-moubarak',
      name: 'AidMoubarakPage',
      component: WishesPage,
      extraProps: { categoryId: 'aid-moubarak' },
    },
    {
      path: '/fr/wishes/vaisakhi',
      name: 'VaisakhiPage',
      component: WishesPage,
      extraProps: { categoryId: 'vaisakhi' },
    },
    {
      path: '/fr/wishes/roch-hachana',
      name: 'RochHachanaPage',
      component: WishesPage,
      extraProps: { categoryId: 'roch-hachana' },
    },
    {
      path: '/fr/wishes/norouz',
      name: 'NorouzPage',
      component: WishesPage,
      extraProps: { categoryId: 'norouz' },
    },
    {
      path: '/fr/wishes/anniversaire-de-mariage-soeur',
      name: 'AnniversaireDeMariageSoeurPage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversaire-de-mariage-soeur' },
    },
    {
      path: '/fr/wishes/anniversaire-de-mariage-amis',
      name: 'AnniversaireDeMariageAmisPage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversaire-de-mariage-amis' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-de-mariage-pour-ma-femme',
      name: 'TexteAnniversaireDeMariagePourMaFemmePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-de-mariage-pour-ma-femme' },
    },
    {
      path: '/fr/wishes/anniversaire-de-mariage-oncle-et-tante',
      name: 'AnniversaireDeMariageOncleEtTantePage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversaire-de-mariage-oncle-et-tante' },
    },
    {
      path: '/fr/wishes/anniversaire-de-mariage-fille',
      name: 'AnniversaireDeMariageFillePage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversaire-de-mariage-fille' },
    },
    {
      path: '/fr/wishes/anniversaire-de-mariage-frere',
      name: 'AnniversaireDeMariageFrerePage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversaire-de-mariage-frere' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-de-mariage-pour-mon-mari',
      name: 'TexteAnniversaireDeMariagePourMonMariPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-de-mariage-pour-mon-mari' },
    },
    {
      path: '/fr/wishes/noces-de-diamant-60-ans',
      name: 'NocesDeDiamant60AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'noces-de-diamant-60-ans' },
    },
    {
      path: '/fr/wishes/noces-de-perle-30-ans',
      name: 'NocesDePerle30AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'noces-de-perle-30-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-5-ans',
      name: 'TexteAnniversaire5AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-5-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-16-ans',
      name: 'TexteAnniversaire16AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-16-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-patron',
      name: 'TexteAnniversairePourPatronPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-patron' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-gendre',
      name: 'TexteAnniversairePourGendrePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-gendre' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-enseignant',
      name: 'TexteAnniversairePourEnseignantPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-enseignant' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-belle-soeur',
      name: 'TexteAnniversairePourBelleSoeurPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-belle-soeur' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-belle-fille',
      name: 'TexteAnniversairePourBelleFillePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-belle-fille' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-beau-frere',
      name: 'TexteAnniversairePourBeauFrerePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-beau-frere' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-90-ans',
      name: 'TexteAnniversaire90AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-90-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-75-ans',
      name: 'TexteAnniversaire75AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-75-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-2-ans',
      name: 'TexteAnniversaire2AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-2-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-13-ans',
      name: 'TexteAnniversaire13AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-13-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-10-ans',
      name: 'TexteAnniversaire10AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-10-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-1-an',
      name: 'TexteAnniversaire1AnPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-1-an' },
    },
    {
      path: '/fr/wishes/noces-d-emeraude-40-ans',
      name: 'NocesDEmeraude40AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'noces-d-emeraude-40-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-65-ans',
      name: 'TexteAnniversaire65AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-65-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-un-ami',
      name: 'TexteAnniversairePourUnAmiPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-un-ami' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-mentor',
      name: 'TexteAnniversairePourMentorPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-mentor' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-marraine',
      name: 'TexteAnniversairePourMarrainePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-marraine' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-filleule',
      name: 'TexteAnniversairePourFilleulePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-filleule' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-fiance',
      name: 'TexteAnniversairePourFiancePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-fiance' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-ami-d-enfance',
      name: 'TexteAnniversairePourAmiDEnfancePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-ami-d-enfance' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-25-ans',
      name: 'TexteAnniversaire25AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-25-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-20-ans',
      name: 'TexteAnniversaire20AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-20-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-beau-pere',
      name: 'TexteAnniversairePourBeauPerePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-beau-pere' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-belle-mere',
      name: 'TexteAnniversairePourBelleMerePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-belle-mere' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-petit-fils',
      name: 'TexteAnniversairePourPetitFilsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-petit-fils' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-niece',
      name: 'TexteAnniversairePourNiecePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-niece' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-neveu',
      name: 'TexteAnniversairePourNeveuPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-neveu' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-cousine',
      name: 'TexteAnniversairePourCousinePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-cousine' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-oncle',
      name: 'TexteAnniversairePourOnclePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-oncle' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-tante',
      name: 'TexteAnniversairePourTantePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-tante' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-grand-papa',
      name: 'TexteAnniversairePourGrandPapaPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-grand-papa' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-mon-mari',
      name: 'TexteAnniversairePourMonMariPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-mon-mari' },
    },
    {
      path: '/fr/wishes/joyeux-anniversaire-au-ciel',
      name: 'JoyeuxAnniversaireAuCielPage',
      component: WishesPage,
      extraProps: { categoryId: 'joyeux-anniversaire-au-ciel' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-chretien',
      name: 'TexteAnniversaireChretienPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-chretien' },
    },
    {
      path: '/fr/wishes/citations-fleurs',
      name: 'CitationsFleursPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-fleurs' },
    },
    {
      path: '/fr/wishes/citations-bonne-nuit',
      name: 'CitationsBonneNuitPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-bonne-nuit' },
    },
    {
      path: '/fr/wishes/citations-bonjour',
      name: 'CitationsBonjourPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-bonjour' },
    },
    {
      path: '/fr/wishes/quoi-ecrire-carte-anniversaire',
      name: 'QuoiEcrireCarteAnniversairePage',
      component: WishesPage,
      extraProps: { categoryId: 'quoi-ecrire-carte-anniversaire' },
    },
    {
      path: '/fr/wishes/noces-d-or-50-ans',
      name: 'NocesDOr50AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'noces-d-or-50-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-ma-blonde',
      name: 'TexteAnniversairePourMaBlondePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-ma-blonde' },
    },
    {
      path: '/fr/wishes/citations-condoleances',
      name: 'CitationsCondoleancesPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-condoleances' },
    },
    {
      path: '/fr/wishes/citations-anniversaire',
      name: 'CitationsAnniversairePage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-anniversaire' },
    },
    {
      path: '/fr/wishes/voeux-de-noel-pour-amis',
      name: 'VoeuxDeNoelPourAmisPage',
      component: WishesPage,
      extraProps: { categoryId: 'voeux-de-noel-pour-amis' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-mon-chum',
      name: 'TexteAnniversairePourMonChumPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-mon-chum' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-ma-femme',
      name: 'TexteAnniversairePourMaFemmePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-ma-femme' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-petite-fille',
      name: 'TexteAnniversairePetiteFillePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-petite-fille' },
    },
    {
      path: '/fr/wishes/quoi-ecrire-carte-de-sympathie',
      name: 'QuoiEcrireCarteDeSympathiePage',
      component: WishesPage,
      extraProps: { categoryId: 'quoi-ecrire-carte-de-sympathie' },
    },
    {
      path: '/fr/wishes/quoi-ecrire-carte-de-remerciement',
      name: 'QuoiEcrireCarteDeRemerciementPage',
      component: WishesPage,
      extraProps: { categoryId: 'quoi-ecrire-carte-de-remerciement' },
    },
    {
      path: '/fr/wishes/quoi-ecrire-carte-de-mariage',
      name: 'QuoiEcrireCarteDeMariagePage',
      component: WishesPage,
      extraProps: { categoryId: 'quoi-ecrire-carte-de-mariage' },
    },
    {
      path: '/fr/wishes/poemes-pour-maman',
      name: 'PoemesPourMamanPage',
      component: WishesPage,
      extraProps: { categoryId: 'poemes-pour-maman' },
    },
    {
      path: '/fr/wishes/noces-d-argent-25-ans',
      name: 'NocesDArgent25AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'noces-d-argent-25-ans' },
    },
    {
      path: '/fr/wishes/messages-de-bonne-nuit',
      name: 'MessagesDeBonneNuitPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-bonne-nuit' },
    },
    {
      path: '/fr/wishes/legendes-de-noel',
      name: 'LegendesDeNoelPage',
      component: WishesPage,
      extraProps: { categoryId: 'legendes-de-noel' },
    },
    {
      path: '/fr/wishes/joyeux-anniversaire-en-retard',
      name: 'JoyeuxAnniversaireEnRetardPage',
      component: WishesPage,
      extraProps: { categoryId: 'joyeux-anniversaire-en-retard' },
    },
    {
      path: '/fr/wishes/joyeuse-hanoucca',
      name: 'JoyeuseHanouccaPage',
      component: WishesPage,
      extraProps: { categoryId: 'joyeuse-hanoucca' },
    },
    {
      path: '/fr/wishes/je-pense-a-toi',
      name: 'JePenseAToiPage',
      component: WishesPage,
      extraProps: { categoryId: 'je-pense-a-toi' },
    },
    {
      path: '/fr/wishes/bonne-annee-2027',
      name: 'BonneAnnee2027Page',
      component: WishesPage,
      extraProps: { categoryId: 'bonne-annee-2027' },
    },
    {
      path: '/fr/wishes/anniversaire-de-deces',
      name: 'AnniversaireDeDecesPage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversaire-de-deces' },
    },
    {
      path: '/fr/wishes/citations-amitie',
      name: 'CitationsAmitiePage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-amitie' },
    },
    {
      path: '/fr/wishes/citations-mariage',
      name: 'CitationsMariagePage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-mariage' },
    },
    {
      path: '/fr/wishes/citations-d-amour',
      name: 'CitationsDAmourPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-d-amour' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-80-ans',
      name: 'TexteAnniversaire80AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-80-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-70-ans',
      name: 'TexteAnniversaire70AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-70-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-60-ans',
      name: 'TexteAnniversaire60AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-60-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-40-ans',
      name: 'TexteAnniversaire40AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-40-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-30-ans',
      name: 'TexteAnniversaire30AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-30-ans' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-18-ans',
      name: 'TexteAnniversaire18AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-18-ans' },
    },
    {
      path: '/fr/wishes/message-bonne-nuit-mon-amour',
      name: 'MessageBonneNuitMonAmourPage',
      component: WishesPage,
      extraProps: { categoryId: 'message-bonne-nuit-mon-amour' },
    },
    {
      path: '/fr/wishes/message-bonjour-mon-amour',
      name: 'MessageBonjourMonAmourPage',
      component: WishesPage,
      extraProps: { categoryId: 'message-bonjour-mon-amour' },
    },
    {
      path: '/fr/wishes/messages-de-bonjour',
      name: 'MessagesDeBonjourPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-bonjour' },
    },
    {
      path: '/fr/wishes/citations-soeur',
      name: 'CitationsSoeurPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-soeur' },
    },
    {
      path: '/fr/wishes/citations-papa',
      name: 'CitationsPapaPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-papa' },
    },
    {
      path: '/fr/wishes/citations-maman',
      name: 'CitationsMamanPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-maman' },
    },
    {
      path: '/fr/wishes/message-condoleances-collegue',
      name: 'MessageCondoleancesColleguePage',
      component: WishesPage,
      extraProps: { categoryId: 'message-condoleances-collegue' },
    },
    {
      path: '/fr/wishes/messages-de-sympathie',
      name: 'MessagesDeSympathiePage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-sympathie' },
    },
    {
      path: '/fr/wishes/citations-deces',
      name: 'CitationsDecesPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-deces' },
    },
    {
      path: '/fr/wishes/citations-sur-la-vie',
      name: 'CitationsSurLaViePage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-sur-la-vie' },
    },
    {
      path: '/quotes/sympathy-quotes',
      name: 'SympathyQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'sympathy-quotes' },
    },
    {
      path: '/quotes/rest-in-peace-quotes',
      name: 'RestInPeaceQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'rest-in-peace-quotes' },
    },
    {
      path: '/quotes/valentines-day-quotes',
      name: 'ValentinesDayQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'valentines-day-quotes' },
    },
    {
      path: '/quotes/wedding-quotes',
      name: 'WeddingQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'wedding-quotes' },
    },
    {
      path: '/quotes/anniversary-quotes',
      name: 'AnniversaryQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversary-quotes' },
    },
    {
      path: '/quotes/thanksgiving-quotes',
      name: 'ThanksgivingQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'thanksgiving-quotes' },
    },
    {
      path: '/quotes/thank-you-quotes',
      name: 'ThankYouQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'thank-you-quotes' },
    },
    {
      path: '/quotes/dad-quotes',
      name: 'DadQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'dad-quotes' },
    },
    {
      path: '/quotes/grandma-quotes',
      name: 'GrandmaQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'grandma-quotes' },
    },
    {
      path: '/quotes/sister-quotes',
      name: 'SisterQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'sister-quotes' },
    },
    {
      path: '/fr/wishes/citations-de-noel',
      name: 'CitationsDeNoelPage',
      component: WishesPage,
      extraProps: { categoryId: 'citations-de-noel' },
    },
    {
      path: '/fr/wishes/poemes-de-noel',
      name: 'PoemesDeNoelPage',
      component: WishesPage,
      extraProps: { categoryId: 'poemes-de-noel' },
    },
    {
      path: '/quotes/good-morning-prayer',
      name: 'GoodMorningPrayerPage',
      component: WishesPage,
      extraProps: { categoryId: 'good-morning-prayer' },
    },
    {
      path: '/wishes/good-morning-blessings',
      name: 'GoodMorningBlessingsPage',
      component: WishesPage,
      extraProps: { categoryId: 'good-morning-blessings' },
    },
    {
      path: '/quotes/good-night-quotes',
      name: 'GoodNightQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'good-night-quotes' },
    },
    {
      path: '/quotes/good-morning-quotes',
      name: 'GoodMorningQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'good-morning-quotes' },
    },
    {
      path: '/fr/wishes/quoi-ecrire-dans-une-carte-de-noel',
      name: 'QuoiEcrireDansUneCarteDeNoelPage',
      component: WishesPage,
      extraProps: { categoryId: 'quoi-ecrire-dans-une-carte-de-noel' },
    },
    {
      path: '/wishes/good-night-messages-for-him',
      name: 'GoodNightMessagesForHimPage',
      component: WishesPage,
      extraProps: { categoryId: 'good-night-messages-for-him' },
    },
    {
      path: '/wishes/good-morning-messages-for-him',
      name: 'GoodMorningMessagesForHimPage',
      component: WishesPage,
      extraProps: { categoryId: 'good-morning-messages-for-him' },
    },
    {
      path: '/wishes/good-night-messages-for-her',
      name: 'GoodNightMessagesForHerPage',
      component: WishesPage,
      extraProps: { categoryId: 'good-night-messages-for-her' },
    },
    {
      path: '/wishes/good-morning-messages-for-her',
      name: 'GoodMorningMessagesForHerPage',
      component: WishesPage,
      extraProps: { categoryId: 'good-morning-messages-for-her' },
    },
    {
      path: '/wishes/good-night-messages',
      name: 'GoodNightMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'good-night-messages' },
    },
    {
      path: '/wishes/good-morning-messages',
      name: 'GoodMorningMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'good-morning-messages' },
    },
    {
      path: '/wishes/christmas-card-messages',
      name: 'ChristmasCardMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'christmas-card-messages' },
    },
    {
      path: '/wishes/what-to-write-in-a-christmas-card',
      name: 'WhatToWriteInAChristmasCardPage',
      component: WishesPage,
      extraProps: { categoryId: 'what-to-write-in-a-christmas-card' },
    },
    {
      path: '/quotes/christmas-quotes',
      name: 'ChristmasQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'christmas-quotes' },
    },
    {
      path: '/wishes/merry-christmas-wishes-for-friends',
      name: 'MerryChristmasWishesForFriendsPage',
      component: WishesPage,
      extraProps: { categoryId: 'merry-christmas-wishes-for-friends' },
    },
    {
      path: '/quotes/christmas-captions',
      name: 'ChristmasCaptionsPage',
      component: WishesPage,
      extraProps: { categoryId: 'christmas-captions' },
    },
    {
      path: '/quotes/christmas-poems',
      name: 'ChristmasPoemsPage',
      component: WishesPage,
      extraProps: { categoryId: 'christmas-poems' },
    },
    {
      path: '/wishes/new-year-messages-2027',
      name: 'NewYearMessages2027Page',
      component: WishesPage,
      extraProps: { categoryId: 'new-year-messages-2027' },
    },
    {
      path: '/fr/wishes',
      name: 'WishesHubPageFr',
      component: WishesPage,
      extraProps: { locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-de-condoleances',
      name: 'MessagesDeCondoleancesPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-condoleances', locale: 'fr' },
    },
    {
      path: '/fr/wishes/souhaits-de-bon-retablissement',
      name: 'SouhaitsDeBonRetablissementPage',
      component: WishesPage,
      extraProps: { categoryId: 'souhaits-de-bon-retablissement', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-d-adieu',
      name: 'MessagesDAdieuPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-d-adieu', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-d-anniversaire',
      name: 'MessagesDAnniversairePage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-d-anniversaire', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-de-remerciement',
      name: 'MessagesDeRemerciementPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-remerciement', locale: 'fr' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-court-et-touchant',
      name: 'TexteAnniversaireCourtEtTouchantPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-court-et-touchant', locale: 'fr' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-sa-fille',
      name: 'TexteAnniversairePourSaFillePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-sa-fille', locale: 'fr' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-son-fils',
      name: 'TexteAnniversairePourSonFilsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-son-fils', locale: 'fr' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-meilleure-amie',
      name: 'TexteAnniversaireMeilleureAmiePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-meilleure-amie', locale: 'fr' },
    },
    {
      path: '/fr/wishes/joyeux-anniversaire-mon-amour',
      name: 'JoyeuxAnniversaireMonAmourPage',
      component: WishesPage,
      extraProps: { categoryId: 'joyeux-anniversaire-mon-amour', locale: 'fr' },
    },
    {
      path: '/fr/wishes/message-de-condoleances-court',
      name: 'MessageDeCondoleancesCourtPage',
      component: WishesPage,
      extraProps: { categoryId: 'message-de-condoleances-court', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-de-mariage',
      name: 'MessagesDeMariagePage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-mariage', locale: 'fr' },
    },
    {
      path: '/fr/wishes/felicitations-naissance',
      name: 'FelicitationsNaissancePage',
      component: WishesPage,
      extraProps: { categoryId: 'felicitations-naissance', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-de-fiancailles',
      name: 'MessagesDeFiancaillesPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-fiancailles', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-de-retraite',
      name: 'MessagesDeRetraitePage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-retraite', locale: 'fr' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-maman',
      name: 'TexteAnniversairePourMamanPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-maman', locale: 'fr' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-papa',
      name: 'TexteAnniversairePourPapaPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-papa', locale: 'fr' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-ma-soeur',
      name: 'TexteAnniversairePourMaSoeurPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-ma-soeur', locale: 'fr' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-mon-frere',
      name: 'TexteAnniversairePourMonFrerePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-mon-frere', locale: 'fr' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-drole',
      name: 'TexteAnniversaireDrolePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-drole', locale: 'fr' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-un-collegue',
      name: 'TexteAnniversairePourUnColleguePage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-un-collegue', locale: 'fr' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-50-ans',
      name: 'TexteAnniversaire50AnsPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-50-ans', locale: 'fr' },
    },
    {
      path: '/fr/wishes/texte-anniversaire-pour-grand-maman',
      name: 'TexteAnniversairePourGrandMamanPage',
      component: WishesPage,
      extraProps: { categoryId: 'texte-anniversaire-pour-grand-maman', locale: 'fr' },
    },
    {
      path: '/fr/wishes/voeux-de-noel',
      name: 'VoeuxDeNoelPage',
      component: WishesPage,
      extraProps: { categoryId: 'voeux-de-noel', locale: 'fr' },
    },
    {
      path: '/fr/wishes/voeux-de-bonne-annee',
      name: 'VoeuxDeBonneAnneePage',
      component: WishesPage,
      extraProps: { categoryId: 'voeux-de-bonne-annee', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-de-saint-valentin',
      name: 'MessagesDeSaintValentinPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-saint-valentin', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-pour-la-fete-des-meres',
      name: 'MessagesPourLaFeteDesMeresPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-pour-la-fete-des-meres', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-pour-la-fete-des-peres',
      name: 'MessagesPourLaFeteDesPeresPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-pour-la-fete-des-peres', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-anniversaire-de-mariage',
      name: 'MessagesAnniversaireDeMariagePage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-anniversaire-de-mariage', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-d-encouragement',
      name: 'MessagesDEncouragementPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-d-encouragement', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-de-cremaillere',
      name: 'MessagesDeCremaillerePage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-cremaillere', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-de-felicitations',
      name: 'MessagesDeFelicitationsPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-felicitations', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-de-bonne-chance',
      name: 'MessagesDeBonneChancePage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-bonne-chance', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-de-diplome',
      name: 'MessagesDeDiplomePage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-de-diplome', locale: 'fr' },
    },
    {
      path: '/fr/wishes/messages-pour-un-nouvel-emploi',
      name: 'MessagesPourUnNouvelEmploiPage',
      component: WishesPage,
      extraProps: { categoryId: 'messages-pour-un-nouvel-emploi', locale: 'fr' },
    },
    {
      path: '/wishes/christmas-wishes',
      name: 'ChristmasWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'christmas-wishes' },
    },
    {
      path: '/wishes/new-year-wishes',
      name: 'NewYearWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'new-year-wishes' },
    },
    {
      path: '/wishes/baby-shower-wishes',
      name: 'BabyShowerWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'baby-shower-wishes' },
    },
    {
      path: '/wishes/50th-birthday-wishes',
      name: 'FiftiethBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '50th-birthday-wishes' },
    },
    {
      path: '/wishes/belated-birthday-wishes',
      name: 'BelatedBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'belated-birthday-wishes' },
    },
    {
      path: '/wishes/birthday-wishes-for-dad',
      name: 'BirthdayWishesForDadPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-dad' },
    },
    {
      path: '/wishes/condolence-messages',
      name: 'CondolenceMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'condolence-messages' },
    },
    {
      path: '/wishes/graduation-wishes',
      name: 'GraduationWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'graduation-wishes' },
    },
    {
      path: '/wishes/engagement-wishes',
      name: 'EngagementWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'engagement-wishes' },
    },
    {
      path: '/wishes/40th-birthday-wishes',
      name: 'FortiethBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '40th-birthday-wishes' },
    },
    {
      path: '/wishes/30th-birthday-wishes',
      name: 'ThirtiethBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '30th-birthday-wishes' },
    },
    {
      path: '/wishes/birthday-wishes-for-aunt',
      name: 'BirthdayWishesForAuntPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-aunt' },
    },
    {
      path: '/wishes/60th-birthday-wishes',
      name: 'SixtiethBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '60th-birthday-wishes' },
    },
    {
      path: '/wishes/70th-birthday-wishes',
      name: 'SeventiethBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '70th-birthday-wishes' },
    },
    {
      path: '/wishes/18th-birthday-wishes',
      name: 'EighteenthBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '18th-birthday-wishes' },
    },
    {
      path: '/wishes/birthday-wishes-for-niece',
      name: 'BirthdayWishesForNiecePage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-niece' },
    },
    {
      path: '/wishes/birthday-wishes-for-nephew',
      name: 'BirthdayWishesForNephewPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-nephew' },
    },
    {
      path: '/wishes/birthday-wishes-for-girlfriend',
      name: 'BirthdayWishesForGirlfriendPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-girlfriend' },
    },
    {
      path: '/wishes/birthday-wishes-for-boyfriend',
      name: 'BirthdayWishesForBoyfriendPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-boyfriend' },
    },
    {
      path: '/wishes/mothers-day-messages',
      name: 'MothersDayMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'mothers-day-messages' },
    },
    {
      path: '/wishes/fathers-day-messages',
      name: 'FathersDayMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'fathers-day-messages' },
    },
    {
      path: '/wishes/thanksgiving-messages',
      name: 'ThanksgivingMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'thanksgiving-messages' },
    },
    {
      path: '/wishes/work-anniversary-wishes',
      name: 'WorkAnniversaryWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'work-anniversary-wishes' },
    },
    {
      path: '/wishes/easter-wishes',
      name: 'EasterWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'easter-wishes' },
    },
    {
      path: '/wishes/valentines-day-messages',
      name: 'ValentinesDayMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'valentines-day-messages' },
    },
    {
      path: '/wishes/sweet-16-birthday-wishes',
      name: 'SweetSixteenBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'sweet-16-birthday-wishes' },
    },
    {
      path: '/wishes/21st-birthday-wishes',
      name: 'TwentyFirstBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '21st-birthday-wishes' },
    },
    {
      path: '/wishes/teacher-appreciation-messages',
      name: 'TeacherAppreciationMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'teacher-appreciation-messages' },
    },
    {
      path: '/wishes/housewarming-wishes',
      name: 'HousewarmingWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'housewarming-wishes' },
    },
    {
      path: '/wishes/diwali-wishes',
      name: 'DiwaliWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'diwali-wishes' },
    },
    {
      path: '/wishes/eid-mubarak-wishes',
      name: 'EidMubarakWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'eid-mubarak-wishes' },
    },
    {
      path: '/wishes/remembrance-day-quotes',
      name: 'RemembranceDayQuotesPage',
      component: WishesPage,
      extraProps: { categoryId: 'remembrance-day-quotes' },
    },
    {
      path: '/wishes/words-of-encouragement',
      name: 'WordsOfEncouragementPage',
      component: WishesPage,
      extraProps: { categoryId: 'words-of-encouragement' },
    },
    {
      path: '/wishes/thinking-of-you-messages',
      name: 'ThinkingOfYouMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'thinking-of-you-messages' },
    },
    {
      path: '/wishes/what-to-write-in-a-birthday-card',
      name: 'WhatToWriteBirthdayCardPage',
      component: WishesPage,
      extraProps: { categoryId: 'what-to-write-in-a-birthday-card' },
    },
    {
      path: '/wishes/what-to-write-in-a-wedding-card',
      name: 'WhatToWriteWeddingCardPage',
      component: WishesPage,
      extraProps: { categoryId: 'what-to-write-in-a-wedding-card' },
    },
    {
      path: '/wishes/what-to-write-in-a-graduation-card',
      name: 'WhatToWriteGraduationCardPage',
      component: WishesPage,
      extraProps: { categoryId: 'what-to-write-in-a-graduation-card' },
    },
    {
      path: '/wishes/what-to-write-in-a-thank-you-card',
      name: 'WhatToWriteThankYouCardPage',
      component: WishesPage,
      extraProps: { categoryId: 'what-to-write-in-a-thank-you-card' },
    },
    {
      path: '/wishes/what-to-write-in-an-anniversary-card',
      name: 'WhatToWriteAnniversaryCardPage',
      component: WishesPage,
      extraProps: { categoryId: 'what-to-write-in-an-anniversary-card' },
    },
    {
      path: '/wishes/what-to-write-on-funeral-flowers',
      name: 'WhatToWriteFuneralFlowersPage',
      component: WishesPage,
      extraProps: { categoryId: 'what-to-write-on-funeral-flowers' },
    },
    {
      path: '/wishes/25th-anniversary-wishes',
      name: 'TwentyFifthAnniversaryWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '25th-anniversary-wishes' },
    },
    {
      path: '/wishes/50th-anniversary-wishes',
      name: 'FiftiethAnniversaryWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '50th-anniversary-wishes' },
    },
    {
      path: '/wishes/new-job-wishes',
      name: 'NewJobWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'new-job-wishes' },
    },
    {
      path: '/wishes/nowruz-wishes',
      name: 'NowruzWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'nowruz-wishes' },
    },
    {
      path: '/wishes/vaisakhi-wishes',
      name: 'VaisakhiWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'vaisakhi-wishes' },
    },
    {
      path: '/wishes/rosh-hashanah-greetings',
      name: 'RoshHashanahGreetingsPage',
      component: WishesPage,
      extraProps: { categoryId: 'rosh-hashanah-greetings' },
    },
    {
      path: '/wishes/hanukkah-wishes',
      name: 'HanukkahWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'hanukkah-wishes' },
    },
    {
      path: '/wishes/ramadan-mubarak-wishes',
      name: 'RamadanMubarakWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'ramadan-mubarak-wishes' },
    },
    {
      path: '/wishes/canada-day-wishes',
      name: 'CanadaDayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'canada-day-wishes' },
    },
    {
      path: '/wishes/family-day-wishes',
      name: 'FamilyDayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'family-day-wishes' },
    },
    {
      path: '/wishes/halloween-wishes',
      name: 'HalloweenWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'halloween-wishes' },
    },
    {
      path: '/wishes/anniversary-messages-for-wife',
      name: 'AnniversaryMessagesForWifePage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversary-messages-for-wife' },
    },
    {
      path: '/wishes/anniversary-messages-for-husband',
      name: 'AnniversaryMessagesForHusbandPage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversary-messages-for-husband' },
    },
    {
      path: '/wishes/anniversary-messages-for-friends',
      name: 'AnniversaryMessagesForFriendsPage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversary-messages-for-friends' },
    },
    {
      path: '/wishes/anniversary-messages-for-sister',
      name: 'AnniversaryMessagesForSisterPage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversary-messages-for-sister' },
    },
    {
      path: '/wishes/anniversary-messages-for-brother',
      name: 'AnniversaryMessagesForBrotherPage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversary-messages-for-brother' },
    },
    {
      path: '/wishes/birthday-wishes-for-brother-in-law',
      name: 'BirthdayWishesForBrotherInLawPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-brother-in-law' },
    },
    {
      path: '/wishes/birthday-wishes-for-sister-in-law',
      name: 'BirthdayWishesForSisterInLawPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-sister-in-law' },
    },
    {
      path: '/wishes/birthday-wishes-for-mother-in-law',
      name: 'BirthdayWishesForMotherInLawPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-mother-in-law' },
    },
    {
      path: '/wishes/birthday-wishes-for-son-in-law',
      name: 'BirthdayWishesForSonInLawPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-son-in-law' },
    },
    {
      path: '/wishes/birthday-wishes-for-grandpa',
      name: 'BirthdayWishesForGrandpaPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-grandpa' },
    },
    {
      path: '/wishes/birthday-wishes-for-childhood-friend',
      name: 'BirthdayWishesForChildhoodFriendPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-childhood-friend' },
    },
    {
      path: '/wishes/farewell-messages-for-boss',
      name: 'FarewellMessagesForBossPage',
      component: WishesPage,
      extraProps: { categoryId: 'farewell-messages-for-boss' },
    },
    {
      path: '/wishes/funny-retirement-wishes',
      name: 'FunnyRetirementWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'funny-retirement-wishes' },
    },
    {
      path: '/wishes/retirement-wishes-for-teacher',
      name: 'RetirementWishesForTeacherPage',
      component: WishesPage,
      extraProps: { categoryId: 'retirement-wishes-for-teacher' },
    },
    {
      path: '/wishes/funny-wedding-wishes',
      name: 'FunnyWeddingWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'funny-wedding-wishes' },
    },
    {
      path: '/wishes/graduation-messages-for-daughter',
      name: 'GraduationMessagesForDaughterPage',
      component: WishesPage,
      extraProps: { categoryId: 'graduation-messages-for-daughter' },
    },
    {
      path: '/wishes/graduation-messages-for-son',
      name: 'GraduationMessagesForSonPage',
      component: WishesPage,
      extraProps: { categoryId: 'graduation-messages-for-son' },
    },
    {
      path: '/wishes/graduation-messages-for-niece',
      name: 'GraduationMessagesForNiecePage',
      component: WishesPage,
      extraProps: { categoryId: 'graduation-messages-for-niece' },
    },
    {
      path: '/wishes/wedding-wishes-for-brother',
      name: 'WeddingWishesForBrotherPage',
      component: WishesPage,
      extraProps: { categoryId: 'wedding-wishes-for-brother' },
    },
    {
      path: '/wishes/wedding-wishes-for-sister',
      name: 'WeddingWishesForSisterPage',
      component: WishesPage,
      extraProps: { categoryId: 'wedding-wishes-for-sister' },
    },
    {
      path: '/wishes/birthday-wishes-for-father-in-law',
      name: 'BirthdayWishesForFatherInLawPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-father-in-law' },
    },
    {
      path: '/wishes/birthday-wishes-for-daughter-in-law',
      name: 'BirthdayWishesForDaughterInLawPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-daughter-in-law' },
    },
    {
      path: '/wishes/birthday-wishes-for-grandson',
      name: 'BirthdayWishesForGrandsonPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-grandson' },
    },
    {
      path: '/wishes/birthday-wishes-for-granddaughter',
      name: 'BirthdayWishesForGranddaughterPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-granddaughter' },
    },
    {
      path: '/wishes/farewell-messages-for-colleague',
      name: 'FarewellMessagesForColleaguePage',
      component: WishesPage,
      extraProps: { categoryId: 'farewell-messages-for-colleague' },
    },
    {
      path: '/wishes/retirement-wishes-for-boss',
      name: 'RetirementWishesForBossPage',
      component: WishesPage,
      extraProps: { categoryId: 'retirement-wishes-for-boss' },
    },
    {
      path: '/wishes/funny-work-anniversary-messages',
      name: 'FunnyWorkAnniversaryMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'funny-work-anniversary-messages' },
    },
    {
      path: '/wishes/birthday-wishes-for-mentor',
      name: 'BirthdayWishesForMentorPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-mentor' },
    },
    {
      path: '/wishes/sympathy-messages-for-coworker',
      name: 'SympathyMessagesForCoworkerPage',
      component: WishesPage,
      extraProps: { categoryId: 'sympathy-messages-for-coworker' },
    },
    {
      path: '/wishes/funny-get-well-soon-messages',
      name: 'FunnyGetWellSoonMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'funny-get-well-soon-messages' },
    },
    {
      path: '/wishes/2nd-birthday-wishes',
      name: 'SecondBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '2nd-birthday-wishes' },
    },
    {
      path: '/wishes/5th-birthday-wishes',
      name: 'FifthBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '5th-birthday-wishes' },
    },
    {
      path: '/wishes/10th-birthday-wishes',
      name: 'TenthBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '10th-birthday-wishes' },
    },
    {
      path: '/wishes/13th-birthday-wishes',
      name: 'ThirteenthBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '13th-birthday-wishes' },
    },
    {
      path: '/wishes/25th-birthday-wishes',
      name: 'TwentyFifthBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '25th-birthday-wishes' },
    },
    {
      path: '/wishes/65th-birthday-wishes',
      name: 'SixtyFifthBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '65th-birthday-wishes' },
    },
    {
      path: '/wishes/75th-birthday-wishes',
      name: 'SeventyFifthBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '75th-birthday-wishes' },
    },
    {
      path: '/wishes/80th-birthday-wishes',
      name: 'EightiethBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '80th-birthday-wishes' },
    },
    {
      path: '/wishes/90th-birthday-wishes',
      name: 'NinetiethBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '90th-birthday-wishes' },
    },
    {
      path: '/wishes/golden-birthday-messages',
      name: 'GoldenBirthdayMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'golden-birthday-messages' },
    },
    {
      path: '/wishes/golden-birthday-meaning',
      name: 'GoldenBirthdayMeaningPage',
      component: WishesPage,
      extraProps: { categoryId: 'golden-birthday-meaning' },
    },
    {
      path: '/wishes/milestone-birthday-messages',
      name: 'MilestoneBirthdayMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'milestone-birthday-messages' },
    },
    {
      path: '/wishes/birthday-wishes-for-godmother',
      name: 'BirthdayWishesForGodmotherPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-godmother' },
    },
    {
      path: '/wishes/birthday-wishes-for-goddaughter',
      name: 'BirthdayWishesForGoddaughterPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-goddaughter' },
    },
    {
      path: '/wishes/birthday-wishes-for-fiance',
      name: 'BirthdayWishesForFiancePage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-fiance' },
    },
    {
      path: '/wishes/30th-anniversary-wishes',
      name: 'ThirtiethAnniversaryWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '30th-anniversary-wishes' },
    },
    {
      path: '/wishes/40th-anniversary-wishes',
      name: 'FortiethAnniversaryWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '40th-anniversary-wishes' },
    },
    {
      path: '/wishes/60th-anniversary-wishes',
      name: 'SixtiethAnniversaryWishesPage',
      component: WishesPage,
      extraProps: { categoryId: '60th-anniversary-wishes' },
    },
    {
      path: '/wishes/anniversary-messages-for-daughter',
      name: 'AnniversaryMessagesForDaughterPage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversary-messages-for-daughter' },
    },
    {
      path: '/wishes/anniversary-messages-for-aunt-and-uncle',
      name: 'AnniversaryMessagesForAuntAndUnclePage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversary-messages-for-aunt-and-uncle' },
    },
    {
      path: '/wishes/graduation-messages-for-sister',
      name: 'GraduationMessagesForSisterPage',
      component: WishesPage,
      extraProps: { categoryId: 'graduation-messages-for-sister' },
    },
    {
      path: '/wishes/graduation-messages-for-nephew',
      name: 'GraduationMessagesForNephewPage',
      component: WishesPage,
      extraProps: { categoryId: 'graduation-messages-for-nephew' },
    },
    {
      path: '/wishes/graduation-messages-for-granddaughter',
      name: 'GraduationMessagesForGranddaughterPage',
      component: WishesPage,
      extraProps: { categoryId: 'graduation-messages-for-granddaughter' },
    },
    {
      path: '/wishes/graduation-messages-for-phd',
      name: 'GraduationMessagesForPhdPage',
      component: WishesPage,
      extraProps: { categoryId: 'graduation-messages-for-phd' },
    },
    {
      path: '/wishes/retirement-wishes-for-dad-or-mom',
      name: 'RetirementWishesForDadOrMomPage',
      component: WishesPage,
      extraProps: { categoryId: 'retirement-wishes-for-dad-or-mom' },
    },
    {
      path: '/wishes/retirement-wishes-for-friend',
      name: 'RetirementWishesForFriendPage',
      component: WishesPage,
      extraProps: { categoryId: 'retirement-wishes-for-friend' },
    },
    {
      path: '/wishes/what-to-write-in-a-retirement-thank-you-card',
      name: 'WhatToWriteRetirementThankYouCardPage',
      component: WishesPage,
      extraProps: { categoryId: 'what-to-write-in-a-retirement-thank-you-card' },
    },
    {
      path: '/wishes/farewell-messages-for-team',
      name: 'FarewellMessagesForTeamPage',
      component: WishesPage,
      extraProps: { categoryId: 'farewell-messages-for-team' },
    },
    {
      path: '/wishes/funny-farewell-messages',
      name: 'FunnyFarewellMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'funny-farewell-messages' },
    },
    {
      path: '/wishes/congratulations-on-passing-exams',
      name: 'CongratulationsOnPassingExamsPage',
      component: WishesPage,
      extraProps: { categoryId: 'congratulations-on-passing-exams' },
    },
    {
      path: '/wishes/wedding-wishes-for-daughter',
      name: 'WeddingWishesForDaughterPage',
      component: WishesPage,
      extraProps: { categoryId: 'wedding-wishes-for-daughter' },
    },
    {
      path: '/wishes/death-anniversary-messages',
      name: 'DeathAnniversaryMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'death-anniversary-messages' },
    },
    {
      path: '/wishes/work-anniversary-milestones',
      name: 'WorkAnniversaryMilestonesPage',
      component: WishesPage,
      extraProps: { categoryId: 'work-anniversary-milestones' },
    },
    {
      path: '/wishes/birthday-wishes-for-friend',
      name: 'BirthdayWishesForFriendPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-friend' },
    },
    {
      path: '/wishes/birthday-wishes-for-mom',
      name: 'BirthdayWishesForMomPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-mom' },
    },
    {
      path: '/wishes/birthday-wishes-for-husband',
      name: 'BirthdayWishesForHusbandPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-husband' },
    },
    {
      path: '/wishes/birthday-wishes-for-wife',
      name: 'BirthdayWishesForWifePage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-wife' },
    },
    {
      path: '/wishes/birthday-wishes-for-sister',
      name: 'BirthdayWishesForSisterPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-sister' },
    },
    {
      path: '/wishes/anniversary-wishes',
      name: 'AnniversaryWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'anniversary-wishes' },
    },
    {
      path: '/wishes/get-well-soon-messages',
      name: 'GetWellSoonMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'get-well-soon-messages' },
    },
    {
      path: '/wishes/what-to-write-in-a-sympathy-card',
      name: 'SympathyCardMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'what-to-write-in-a-sympathy-card' },
    },
    {
      path: '/wishes/birthday-wishes',
      name: 'BirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes' },
    },
    {
      path: '/wishes/birthday-wishes-for-brother',
      name: 'BirthdayWishesForBrotherPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-brother' },
    },
    {
      path: '/wishes/birthday-wishes-for-daughter',
      name: 'BirthdayWishesForDaughterPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-daughter' },
    },
    {
      path: '/wishes/birthday-wishes-for-son',
      name: 'BirthdayWishesForSonPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-son' },
    },
    {
      path: '/wishes/funny-birthday-wishes',
      name: 'FunnyBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'funny-birthday-wishes' },
    },
    {
      path: '/wishes/short-birthday-wishes',
      name: 'ShortBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'short-birthday-wishes' },
    },
    {
      path: '/wishes/wedding-wishes',
      name: 'WeddingWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'wedding-wishes' },
    },
    {
      path: '/wishes/thank-you-messages',
      name: 'ThankYouMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'thank-you-messages' },
    },
    {
      path: '/wishes/new-baby-wishes',
      name: 'NewBabyWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'new-baby-wishes' },
    },
    {
      path: '/wishes/retirement-wishes',
      name: 'RetirementWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'retirement-wishes' },
    },
    {
      path: '/wishes/congratulations-messages',
      name: 'CongratulationsMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'congratulations-messages' },
    },
    {
      path: '/wishes/birthday-wishes-for-boss',
      name: 'BirthdayWishesForBossPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-boss' },
    },
    {
      path: '/wishes/birthday-wishes-for-grandma',
      name: 'BirthdayWishesForGrandmaPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-grandma' },
    },
    {
      path: '/wishes/birthday-wishes-for-teacher',
      name: 'BirthdayWishesForTeacherPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-teacher' },
    },
    {
      path: '/wishes/birthday-wishes-for-coworker',
      name: 'BirthdayWishesForCoworkerPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-coworker' },
    },
    {
      path: '/wishes/birthday-wishes-for-uncle',
      name: 'BirthdayWishesForUnclePage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-uncle' },
    },
    {
      path: '/wishes/birthday-wishes-for-cousin',
      name: 'BirthdayWishesForCousinPage',
      component: WishesPage,
      extraProps: { categoryId: 'birthday-wishes-for-cousin' },
    },
    {
      path: '/wishes/farewell-messages',
      name: 'FarewellMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'farewell-messages' },
    },
    {
      path: '/wishes/good-luck-messages',
      name: 'GoodLuckMessagesPage',
      component: WishesPage,
      extraProps: { categoryId: 'good-luck-messages' },
    },
    {
      path: '/wishes/thank-you-for-birthday-wishes',
      name: 'ThankYouForBirthdayWishesPage',
      component: WishesPage,
      extraProps: { categoryId: 'thank-you-for-birthday-wishes' },
    },
    {
      path: '/blog/national-flower-day-canada',
      name: 'NationalFlowerDayCanadaBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: nationalFlowerDayCanadaBlogArticlePageId },
    },
    {
      path: '/blog/15-most-popular-flowers-canada',
      name: 'MostPopularFlowersCanadaBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: mostPopularFlowersCanadaBlogArticlePageId },
    },
    {
      path: '/blog/12-beautiful-indoor-blooming-plants',
      name: 'IndoorBloomingPlantsBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: indoorBloomingPlantsBlogArticlePageId },
    },
    {
      path: '/blog/how-to-ask-someone-to-be-your-valentine',
      name: 'HowToAskValentineBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToAskValentineBlogArticlePageId },
    },
    {
      path: '/blog/valentines-day-canada',
      name: 'ValentinesDayCanadaBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: valentinesDayCanadaBlogArticlePageId },
    },
    {
      path: '/blog/when-is-mothers-day-in-canada',
      name: 'MothersDayCanadaBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: mothersDayCanadaBlogArticlePageId },
    },
    {
      path: '/blog/bridal-shower-ideas-toronto',
      name: 'BridalShowerIdeasTorontoBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: bridalShowerIdeasTorontoBlogArticlePageId },
    },
    {
      path: '/blog/how-long-do-helium-balloons-last',
      name: 'HowLongDoHeliumBalloonsLastBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howLongDoHeliumBalloonsLastBlogArticlePageId },
    },
    {
      path: '/blog/how-to-deflate-foil-balloons',
      name: 'HowToDeflateFoilBalloonsBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToDeflateFoilBalloonsBlogArticlePageId },
    },
    {
      path: '/blog/flower-meanings',
      name: 'FlowerMeaningsHubBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: flowerMeaningsHubBlogArticlePageId },
    },
    {
      path: '/blog/birth-flowers',
      name: 'BirthFlowersHubOldBlogArticlePage',
      component: RedirectToBirthFlowersHubBlogArticlePage,
    },
    {
      path: '/blog/birth-flowers-by-month',
      name: 'BirthFlowersHubBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: birthFlowersHubBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-red-roses',
      name: 'MeaningOfRedRosesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfRedRosesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-yellow-roses',
      name: 'MeaningOfYellowRosesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfYellowRosesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-snowdrops',
      name: 'MeaningOfSnowdropsBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfSnowdropsBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-carnations',
      name: 'MeaningOfCarnationsBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfCarnationsBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-primroses',
      name: 'MeaningOfPrimrosesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfPrimrosesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-violets',
      name: 'MeaningOfVioletsBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfVioletsBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-daffodil',
      name: 'MeaningOfDaffodilBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfDaffodilBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-sweet-pea',
      name: 'MeaningOfSweetPeaBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfSweetPeaBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-daisy',
      name: 'MeaningOfDaisyBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfDaisyBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-hawthorn',
      name: 'MeaningOfHawthornBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfHawthornBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-lily-of-the-valley',
      name: 'MeaningOfLilyOfTheValleyBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfLilyOfTheValleyBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-white-lilies',
      name: 'MeaningOfWhiteLiliesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfWhiteLiliesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-pink-lilies',
      name: 'MeaningOfPinkLiliesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfPinkLiliesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-yellow-lilies',
      name: 'MeaningOfYellowLiliesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfYellowLiliesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-orange-lilies',
      name: 'MeaningOfOrangeLiliesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfOrangeLiliesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-red-lilies',
      name: 'MeaningOfRedLiliesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfRedLiliesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-purple-lilies',
      name: 'MeaningOfPurpleLiliesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfPurpleLiliesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-black-lilies',
      name: 'MeaningOfBlackLiliesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfBlackLiliesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-calla-lilies',
      name: 'MeaningOfCallaLiliesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfCallaLiliesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-stargazer-lilies',
      name: 'MeaningOfStargazerLiliesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfStargazerLiliesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-tiger-lilies',
      name: 'MeaningOfTigerLiliesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfTigerLiliesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-easter-lilies',
      name: 'MeaningOfEasterLiliesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfEasterLiliesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-orchids',
      name: 'MeaningOfOrchidsBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfOrchidsBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-hydrangeas',
      name: 'MeaningOfHydrangeasBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfHydrangeasBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-honeysuckle',
      name: 'MeaningOfHoneysuckleBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfHoneysuckleBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-rose',
      name: 'MeaningOfRoseBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfRoseBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-water-lily',
      name: 'MeaningOfWaterLilyBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfWaterLilyBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-larkspur',
      name: 'MeaningOfLarkspurBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfLarkspurBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-poppy',
      name: 'MeaningOfPoppyBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfPoppyBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-gladiolus',
      name: 'MeaningOfGladiolusBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfGladiolusBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-morning-glory',
      name: 'MeaningOfMorningGloryBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfMorningGloryBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-aster',
      name: 'MeaningOfAsterBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfAsterBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-cosmos',
      name: 'MeaningOfCosmosBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfCosmosBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-marigold',
      name: 'MeaningOfMarigoldBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfMarigoldBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-peony',
      name: 'MeaningOfPeonyBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfPeonyBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-chrysanthemum',
      name: 'MeaningOfChrysanthemumBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfChrysanthemumBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-holly',
      name: 'MeaningOfHollyBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfHollyBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-narcissus',
      name: 'MeaningOfNarcissusBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfNarcissusBlogArticlePageId },
    },
    {
      path: '/blog/top-flower-shops-toronto-2026',
      name: 'TopFlowerShopsToronto2026BlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: topFlowerShopsToronto2026BlogArticlePageId },
    },
    {
      path: '/blog/january-birth-flower',
      name: 'JanuaryBirthFlowerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: januaryBirthFlowerBlogArticlePageId },
    },
    {
      path: '/blog/february-birth-flower',
      name: 'FebruaryBirthFlowerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: februaryBirthFlowerBlogArticlePageId },
    },
    {
      path: '/blog/march-birth-flower',
      name: 'MarchBirthFlowerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: marchBirthFlowerBlogArticlePageId },
    },
    {
      path: '/blog/april-birth-flower',
      name: 'AprilBirthFlowerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: aprilBirthFlowerBlogArticlePageId },
    },
    {
      path: '/blog/may-birth-flower',
      name: 'MayBirthFlowerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: mayBirthFlowerBlogArticlePageId },
    },
    {
      path: '/blog/june-birth-flower',
      name: 'JuneBirthFlowerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: juneBirthFlowerBlogArticlePageId },
    },
    {
      path: '/blog/july-birth-flower',
      name: 'JulyBirthFlowerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: julyBirthFlowerBlogArticlePageId },
    },
    {
      path: '/blog/august-birth-flower',
      name: 'AugustBirthFlowerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: augustBirthFlowerBlogArticlePageId },
    },
    {
      path: '/blog/september-birth-flower',
      name: 'SeptemberBirthFlowerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: septemberBirthFlowerBlogArticlePageId },
    },
    {
      path: '/blog/october-birth-flower',
      name: 'OctoberBirthFlowerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: octoberBirthFlowerBlogArticlePageId },
    },
    {
      path: '/blog/november-birth-flower',
      name: 'NovemberBirthFlowerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: novemberBirthFlowerBlogArticlePageId },
    },
    {
      path: '/blog/december-birth-flower',
      name: 'DecemberBirthFlowerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: decemberBirthFlowerBlogArticlePageId },
    },
    {
      path: '/blog/sympathy-card-messages',
      name: 'SympathyCardMessagesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: sympathyCardMessagesBlogArticlePageId },
    },
    {
      path: '/blog/how-to-keep-cut-flowers-fresh',
      name: 'HowToKeepCutFlowersFreshBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToKeepCutFlowersFreshBlogArticlePageId },
    },
    {
      path: '/blog/how-to-make-flowers-last-longer',
      name: 'HowToMakeFlowersLastLongerBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToMakeFlowersLastLongerBlogArticlePageId },
    },
    {
      path: '/blog/how-long-do-cut-flowers-last',
      name: 'HowLongDoCutFlowersLastBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howLongDoCutFlowersLastBlogArticlePageId },
    },
    {
      path: '/blog/what-to-put-in-flower-water',
      name: 'WhatToPutInFlowerWaterBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: whatToPutInFlowerWaterBlogArticlePageId },
    },
    {
      path: '/blog/apology-flowers',
      name: 'ApologyFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: apologyFlowersBlogArticlePageId },
    },
    {
      path: '/blog/how-to-care-for-orchids',
      name: 'HowToCareForOrchidsBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToCareForOrchidsBlogArticlePageId },
    },
    {
      path: '/blog/how-to-care-for-tulips',
      name: 'HowToCareForTulipsBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToCareForTulipsBlogArticlePageId },
    },
    {
      path: '/blog/how-to-care-for-peonies',
      name: 'HowToCareForPeoniesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToCareForPeoniesBlogArticlePageId },
    },
    {
      path: '/blog/how-to-care-for-hydrangeas',
      name: 'HowToCareForHydrangeasBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToCareForHydrangeasBlogArticlePageId },
    },
    {
      path: '/blog/how-to-care-for-roses',
      name: 'HowToCareForRosesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToCareForRosesBlogArticlePageId },
    },
    {
      path: '/blog/how-much-do-flowers-cost',
      name: 'HowMuchDoFlowersCostBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howMuchDoFlowersCostBlogArticlePageId },
    },
    {
      path: '/blog/how-to-choose-a-florist',
      name: 'HowToChooseAFloristBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToChooseAFloristBlogArticlePageId },
    },
    {
      path: '/blog/best-flowers-for-a-first-date',
      name: 'BestFlowersForAFirstDateBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: bestFlowersForAFirstDateBlogArticlePageId },
    },
    {
      path: '/blog/wedding-flowers-on-a-budget',
      name: 'WeddingFlowersOnABudgetBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: weddingFlowersOnABudgetBlogArticlePageId },
    },
    {
      path: '/blog/are-flower-subscriptions-worth-it',
      name: 'AreFlowerSubscriptionsWorthItBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: areFlowerSubscriptionsWorthItBlogArticlePageId },
    },
    {
      path: '/blog/how-to-care-for-sunflowers',
      name: 'HowToCareForSunflowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToCareForSunflowersBlogArticlePageId },
    },
    {
      path: '/blog/how-to-care-for-lilies',
      name: 'HowToCareForLiliesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToCareForLiliesBlogArticlePageId },
    },
    {
      path: '/blog/how-to-care-for-carnations',
      name: 'HowToCareForCarnationsBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToCareForCarnationsBlogArticlePageId },
    },
    {
      path: '/blog/how-to-care-for-gerbera-daisies',
      name: 'HowToCareForGerberaDaisiesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToCareForGerberaDaisiesBlogArticlePageId },
    },
    {
      path: '/blog/how-to-care-for-ranunculus',
      name: 'HowToCareForRanunculusBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToCareForRanunculusBlogArticlePageId },
    },
    {
      path: '/blog/best-flowers-for-mothers-day',
      name: 'BestFlowersForMothersDayBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: bestFlowersForMothersDayBlogArticlePageId },
    },
    {
      path: '/blog/fathers-day-flowers-canada',
      name: 'FathersDayFlowersCanadaBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: fathersDayFlowersCanadaBlogArticlePageId },
    },
    {
      path: '/blog/thanksgiving-flowers-canada',
      name: 'ThanksgivingFlowersCanadaBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: thanksgivingFlowersCanadaBlogArticlePageId },
    },
    {
      path: '/blog/remembrance-day-flowers',
      name: 'RemembranceDayFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: remembranceDayFlowersBlogArticlePageId },
    },
    {
      path: '/blog/canada-day-flowers',
      name: 'CanadaDayFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: canadaDayFlowersBlogArticlePageId },
    },
    {
      path: '/blog/lunar-new-year-flowers',
      name: 'LunarNewYearFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: lunarNewYearFlowersBlogArticlePageId },
    },
    {
      path: '/blog/diwali-flowers',
      name: 'DiwaliFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: diwaliFlowersBlogArticlePageId },
    },
    {
      path: '/blog/how-to-care-for-fresh-flowers',
      name: 'HowToCareForFreshFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToCareForFreshFlowersBlogArticlePageId },
    },
    {
      path: '/blog/flowers-for-anniversary',
      name: 'FlowersForAnniversaryBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: flowersForAnniversaryBlogArticlePageId },
    },
    {
      path: '/blog/flowers-for-birthday',
      name: 'FlowersForBirthdayBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: flowersForBirthdayBlogArticlePageId },
    },
    {
      path: '/blog/flowers-for-get-well',
      name: 'FlowersForGetWellBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: flowersForGetWellBlogArticlePageId },
    },
    {
      path: '/blog/flowers-for-new-baby',
      name: 'FlowersForNewBabyBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: flowersForNewBabyBlogArticlePageId },
    },
    {
      path: '/blog/flowers-for-housewarming',
      name: 'FlowersForHousewarmingBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: flowersForHousewarmingBlogArticlePageId },
    },
    {
      path: '/blog/spring-flowers',
      name: 'SpringFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: springFlowersBlogArticlePageId },
    },
    {
      path: '/blog/summer-flowers',
      name: 'SummerFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: summerFlowersBlogArticlePageId },
    },
    {
      path: '/blog/fall-flowers',
      name: 'FallFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: fallFlowersBlogArticlePageId },
    },
    {
      path: '/blog/winter-flowers',
      name: 'WinterFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: winterFlowersBlogArticlePageId },
    },
    {
      path: '/blog/types-of-flowers',
      name: 'TypesOfFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: typesOfFlowersBlogArticlePageId },
    },
    {
      path: '/blog/red-flowers',
      name: 'RedFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: redFlowersBlogArticlePageId },
    },
    {
      path: '/blog/pink-flowers',
      name: 'PinkFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: pinkFlowersBlogArticlePageId },
    },
    {
      path: '/blog/yellow-flowers',
      name: 'YellowFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: yellowFlowersBlogArticlePageId },
    },
    {
      path: '/blog/white-flowers',
      name: 'WhiteFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: whiteFlowersBlogArticlePageId },
    },
    {
      path: '/blog/purple-flowers',
      name: 'PurpleFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: purpleFlowersBlogArticlePageId },
    },
    {
      path: '/blog/christmas-flowers',
      name: 'ChristmasFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: christmasFlowersBlogArticlePageId },
    },
    {
      path: '/blog/easter-flowers',
      name: 'EasterFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: easterFlowersBlogArticlePageId },
    },
    {
      path: '/blog/types-of-roses',
      name: 'TypesOfRosesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: typesOfRosesBlogArticlePageId },
    },
    {
      path: '/blog/best-smelling-flowers',
      name: 'BestSmellingFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: bestSmellingFlowersBlogArticlePageId },
    },
    {
      path: '/blog/flowers-for-funeral',
      name: 'FlowersForFuneralBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: flowersForFuneralBlogArticlePageId },
    },
    {
      path: '/blog/blue-flowers',
      name: 'BlueFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: blueFlowersBlogArticlePageId },
    },
    {
      path: '/blog/orange-flowers',
      name: 'OrangeFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: orangeFlowersBlogArticlePageId },
    },
    {
      path: '/blog/tropical-flowers',
      name: 'TropicalFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: tropicalFlowersBlogArticlePageId },
    },
    {
      path: '/blog/exotic-flowers',
      name: 'ExoticFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: exoticFlowersBlogArticlePageId },
    },
    {
      path: '/blog/types-of-tulips',
      name: 'TypesOfTulipsBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: typesOfTulipsBlogArticlePageId },
    },
    {
      path: '/blog/green-flowers',
      name: 'GreenFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: greenFlowersBlogArticlePageId },
    },
    {
      path: '/blog/black-flowers',
      name: 'BlackFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: blackFlowersBlogArticlePageId },
    },
    {
      path: '/blog/dried-flowers',
      name: 'DriedFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: driedFlowersBlogArticlePageId },
    },
    {
      path: '/blog/forever-roses',
      name: 'ForeverRosesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: foreverRosesBlogArticlePageId },
    },
    {
      path: '/blog/preserved-flowers',
      name: 'PreservedFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: preservedFlowersBlogArticlePageId },
    },
    {
      path: '/blog/rose-petals',
      name: 'RosePetalsBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: rosePetalsBlogArticlePageId },
    },
    {
      path: '/blog/artificial-flowers',
      name: 'ArtificialFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: artificialFlowersBlogArticlePageId },
    },
    {
      path: '/blog/flowers-for-graduation',
      name: 'FlowersForGraduationBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: flowersForGraduationBlogArticlePageId },
    },
    {
      path: '/blog/types-of-peonies',
      name: 'TypesOfPeoniesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: typesOfPeoniesBlogArticlePageId },
    },
    {
      path: '/blog/flowers-that-look-like-roses',
      name: 'FlowersThatLookLikeRosesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: flowersThatLookLikeRosesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-white-roses',
      name: 'MeaningOfWhiteRosesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfWhiteRosesBlogArticlePageId },
    },
    {
      path: '/blog/meaning-of-pink-roses',
      name: 'MeaningOfPinkRosesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: meaningOfPinkRosesBlogArticlePageId },
    },
    {
      path: '/blog/rose-color-meaning',
      name: 'RoseColorMeaningBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: roseColorMeaningBlogArticlePageId },
    },
    {
      path: '/blog/number-of-roses-meaning',
      name: 'NumberOfRosesMeaningBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: numberOfRosesMeaningBlogArticlePageId },
    },
    {
      path: '/blog/types-of-sunflowers',
      name: 'TypesOfSunflowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: typesOfSunflowersBlogArticlePageId },
    },
    {
      path: '/blog/how-to-dry-flowers',
      name: 'HowToDryFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToDryFlowersBlogArticlePageId },
    },
    {
      path: '/blog/wildflower-bouquet',
      name: 'WildflowerBouquetBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: wildflowerBouquetBlogArticlePageId },
    },
    {
      path: '/blog/how-to-dry-roses',
      name: 'HowToDryRosesBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToDryRosesBlogArticlePageId },
    },
    {
      path: '/blog/how-to-wrap-flowers',
      name: 'HowToWrapFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToWrapFlowersBlogArticlePageId },
    },
    {
      path: '/blog/flower-preservation',
      name: 'FlowerPreservationBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: flowerPreservationBlogArticlePageId },
    },
    {
      path: '/blog/how-to-press-flowers',
      name: 'HowToPressFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: howToPressFlowersBlogArticlePageId },
    },
    {
      path: '/blog/flowers-toxic-to-cats',
      name: 'FlowersToxicToCatsBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: flowersToxicToCatsBlogArticlePageId },
    },
    {
      path: '/blog/nowruz-flowers',
      name: 'NowruzFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: nowruzFlowersBlogArticlePageId },
    },
    {
      path: '/blog/peony-season-canada',
      name: 'PeonySeasonCanadaBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: peonySeasonCanadaBlogArticlePageId },
    },
    {
      path: '/blog/prom-flowers',
      name: 'PromFlowersBlogArticlePage',
      component: BlogArticlePage,
      extraProps: { pageId: promFlowersBlogArticlePageId },
    },
    {
      path: '/cart',
      name: 'CartPage',
      component: CatalogCartPage,
    },
    // Firebase-catalog checkout: one page per shop × delivery method.
    {
      path: '/checkout/c/:shopId/:method',
      name: 'CatalogCheckoutPage',
      component: CatalogCheckoutPage,
    },
    {
      path: '/p/flower-delivery-mississauga',
      name: 'MississaugaFlowerDeliveryLegacyCMSPage',
      component: RedirectToMississaugaFlowerDeliveryPage,
    },
    {
      path: '/p/flower-delivery-toronto',
      name: 'TorontoFlowerDeliveryLegacyCMSPage',
      component: RedirectToTorontoFlowerDeliveryPage,
    },
    {
      path: '/p/flower-delivery-vaughan',
      name: 'VaughanFlowerDeliveryLegacyCMSPage',
      component: RedirectToVaughanFlowerDeliveryPage,
    },
    {
      path: '/p/flower-delivery-markham',
      name: 'MarkhamFlowerDeliveryLegacyCMSPage',
      component: RedirectToMarkhamFlowerDeliveryPage,
    },
    {
      path: '/p/flower-delivery-richmond-hill',
      name: 'RichmondHillFlowerDeliveryLegacyCMSPage',
      component: RedirectToRichmondHillFlowerDeliveryPage,
    },
    {
      path: '/p/flower-delivery-brampton',
      name: 'BramptonFlowerDeliveryLegacyCMSPage',
      component: RedirectToBramptonFlowerDeliveryPage,
    },
    {
      path: '/p/flower-delivery-burlington',
      name: 'BurlingtonFlowerDeliveryLegacyCMSPage',
      component: RedirectToBurlingtonFlowerDeliveryPage,
    },
    {
      path: '/p/flower-delivery-north-york',
      name: 'NorthYorkFlowerDeliveryLegacyCMSPage',
      component: RedirectToNorthYorkFlowerDeliveryPage,
    },
    {
      path: '/p/flower-delivery-scarborough',
      name: 'ScarboroughFlowerDeliveryLegacyCMSPage',
      component: RedirectToScarboroughFlowerDeliveryPage,
    },
    {
      path: '/p/flower-delivery-oakville',
      name: 'OakvilleFlowerDeliveryLegacyCMSPage',
      component: RedirectToOakvilleFlowerDeliveryPage,
    },
    {
      path: '/p/flower-delivery-etobicoke',
      name: 'EtobicokeFlowerDeliveryLegacyCMSPage',
      component: RedirectToEtobicokeFlowerDeliveryPage,
    },
    {
      path: '/p/flower-delivery-port-credit',
      name: 'PortCreditFlowerDeliveryLegacyCMSPageV2',
      component: RedirectToPortCreditFlowerDeliveryPage,
    },
    {
      path: '/p/birthday-flowers',
      name: 'BirthdayFlowersLegacyCMSPage',
      component: RedirectToTorontoBirthdayFlowersPage,
    },
    {
      path: '/p/anniversary-flowers',
      name: 'AnniversaryFlowersLegacyCMSPage',
      component: RedirectToTorontoAnniversaryFlowersPage,
    },
    {
      path: '/p/get-well-flowers',
      name: 'GetWellFlowersLegacyCMSPage',
      component: RedirectToTorontoGetWellFlowersPage,
    },
    {
      path: '/p/thank-you-flowers',
      name: 'ThankYouFlowersLegacyCMSPage',
      component: RedirectToTorontoThankYouFlowersPage,
    },
    {
      path: '/p/wedding-flowers',
      name: 'WeddingFlowersLegacyCMSPage',
      component: RedirectToTorontoWeddingFlowersPage,
    },
    {
      path: '/p/birthday-flowers-toronto',
      name: 'TorontoBirthdayFlowersLegacyCMSPage',
      component: RedirectToTorontoBirthdayFlowersPage,
    },
    {
      path: '/p/anniversary-flowers-toronto',
      name: 'TorontoAnniversaryFlowersLegacyCMSPage',
      component: RedirectToTorontoAnniversaryFlowersPage,
    },
    {
      path: '/p/get-well-flowers-toronto',
      name: 'TorontoGetWellFlowersLegacyCMSPage',
      component: RedirectToTorontoGetWellFlowersPage,
    },
    {
      path: '/p/thank-you-flowers-toronto',
      name: 'TorontoThankYouFlowersLegacyCMSPage',
      component: RedirectToTorontoThankYouFlowersPage,
    },
    {
      path: '/p/wedding-flowers-toronto',
      name: 'TorontoWeddingFlowersLegacyCMSPage',
      component: RedirectToTorontoWeddingFlowersPage,
    },
    {
      path: '/p/wedding-flowers-mississauga',
      name: 'MississaugaWeddingFlowersLegacyCMSPage',
      component: RedirectToMississaugaWeddingFlowersPage,
    },
    {
      path: '/p/cakes-toronto',
      name: 'TorontoCakesLegacyCMSPage',
      component: RedirectToTorontoCakesDeliveryPage,
    },
    {
      path: '/p/bento-cake-toronto',
      name: 'TorontoBentoCakeLegacyCMSPage',
      component: RedirectToTorontoBentoCakesDeliveryPage,
    },
    {
      path: '/p/chocolate-covered-strawberries-delivery',
      name: 'TorontoChocolateCoveredStrawberriesLegacyCMSPage',
      component: RedirectToTorontoChocolateCoveredStrawberriesDeliveryPage,
    },
    {
      path: '/p/balloon-arch-toronto',
      name: 'TorontoBalloonArchLegacyCMSPage',
      component: RedirectToTorontoBalloonArchDecorPage,
    },
    {
      path: '/p/balloon-garland-toronto',
      name: 'TorontoBalloonGarlandLegacyCMSPage',
      component: RedirectToTorontoBalloonGarlandDecorPage,
    },
    {
      path: '/p/birthday-balloon-decor-toronto',
      name: 'TorontoBirthdayBalloonDecorLegacyCMSPage',
      component: RedirectToTorontoBirthdayBalloonDecorPage,
    },
    {
      path: '/p/baby-shower-toronto',
      name: 'TorontoBabyShowerLegacyCMSPage',
      component: RedirectToTorontoBabyShowerDecorPage,
    },
    {
      path: '/p/baby-shower-decorations-toronto',
      name: 'TorontoBabyShowerDecorLegacyCMSPage',
      component: RedirectToTorontoBabyShowerDecorPage,
    },
    {
      path: '/p/baby-shower-balloon-decor-toronto',
      name: 'TorontoBabyShowerBalloonDecorLegacyCMSPage',
      component: RedirectToTorontoBabyShowerDecorPage,
    },
    {
      path: '/p/baby-boy-shower-decorations-toronto',
      name: 'TorontoBabyBoyShowerDecorLegacyCMSPage',
      component: RedirectToTorontoBabyBoyShowerDecorPage,
    },
    {
      path: '/p/baby-girl-shower-decorations-toronto',
      name: 'TorontoBabyGirlShowerDecorLegacyCMSPage',
      component: RedirectToTorontoBabyGirlShowerDecorPage,
    },
    {
      path: '/p/bridal-shower-decorations',
      name: 'TorontoBridalShowerDecorLegacyCMSPage',
      component: RedirectToTorontoBridalShowerDecorPage,
    },
    {
      path: '/p/categories-gta',
      name: 'CategoriesGTALegacyCMSPage',
      component: RedirectToLandingPagePermanently,
    },
    {
      path: '/p/blog',
      name: 'BlogLegacyCMSPage',
      component: RedirectToBlogHubPage,
    },
    {
      path: '/p/national-flower-day-canada',
      name: 'NationalFlowerDayCanadaLegacyCMSPage',
      component: RedirectToNationalFlowerDayCanadaBlogArticlePage,
    },
    {
      path: '/p/15-most-popular-flowers-canada',
      name: 'MostPopularFlowersCanadaLegacyCMSPage',
      component: RedirectToMostPopularFlowersCanadaBlogArticlePage,
    },
    {
      path: '/p/12-beautiful-indoor-blooming-plants',
      name: 'IndoorBloomingPlantsLegacyCMSPage',
      component: RedirectToIndoorBloomingPlantsBlogArticlePage,
    },
    {
      path: '/p/how-to-ask-someone-to-be-your-valentine',
      name: 'HowToAskValentineLegacyCMSPage',
      component: RedirectToHowToAskValentineBlogArticlePage,
    },
    {
      path: '/p/valentines-day-canada',
      name: 'ValentinesDayCanadaLegacyCMSPage',
      component: RedirectToValentinesDayCanadaBlogArticlePage,
    },
    {
      path: '/p/when-is-mothers-day-in-canada',
      name: 'MothersDayCanadaLegacyCMSPage',
      component: RedirectToMothersDayCanadaBlogArticlePage,
    },
    {
      path: '/p/how-long-do-helium-balloons-last',
      name: 'HowLongDoHeliumBalloonsLastLegacyCMSPage',
      component: RedirectToHowLongDoHeliumBalloonsLastBlogArticlePage,
    },
    {
      path: '/p/how-to-deflate-foil-balloons',
      name: 'HowToDeflateFoilBalloonsLegacyCMSPage',
      component: RedirectToHowToDeflateFoilBalloonsBlogArticlePage,
    },
    {
      path: '/p/flower-meanings',
      name: 'FlowerMeaningsHubLegacyCMSPage',
      component: RedirectToFlowerMeaningsHubBlogArticlePage,
    },
    {
      path: '/p/birth-flowers',
      name: 'BirthFlowersHubLegacyCMSPage',
      component: RedirectToBirthFlowersHubBlogArticlePage,
    },
    {
      path: '/p/meaning-of-red-roses',
      name: 'MeaningOfRedRosesLegacyCMSPage',
      component: RedirectToMeaningOfRedRosesBlogArticlePage,
    },
    {
      path: '/p/meaning-of-yellow-roses',
      name: 'MeaningOfYellowRosesLegacyCMSPage',
      component: RedirectToMeaningOfYellowRosesBlogArticlePage,
    },
    {
      path: '/p/meaning-of-snowdrops',
      name: 'MeaningOfSnowdropsLegacyCMSPage',
      component: RedirectToMeaningOfSnowdropsBlogArticlePage,
    },
    {
      path: '/p/meaning-of-carnations',
      name: 'MeaningOfCarnationsLegacyCMSPage',
      component: RedirectToMeaningOfCarnationsBlogArticlePage,
    },
    {
      path: '/p/meaning-of-primroses',
      name: 'MeaningOfPrimrosesLegacyCMSPage',
      component: RedirectToMeaningOfPrimrosesBlogArticlePage,
    },
    {
      path: '/p/meaning-of-violets',
      name: 'MeaningOfVioletsLegacyCMSPage',
      component: RedirectToMeaningOfVioletsBlogArticlePage,
    },
    {
      path: '/p/meaning-of-daffodil',
      name: 'MeaningOfDaffodilLegacyCMSPage',
      component: RedirectToMeaningOfDaffodilBlogArticlePage,
    },
    {
      path: '/p/meaning-of-sweet-pea',
      name: 'MeaningOfSweetPeaLegacyCMSPage',
      component: RedirectToMeaningOfSweetPeaBlogArticlePage,
    },
    {
      path: '/p/meaning-of-daisy',
      name: 'MeaningOfDaisyLegacyCMSPage',
      component: RedirectToMeaningOfDaisyBlogArticlePage,
    },
    {
      path: '/p/meaning-of-hawthorn',
      name: 'MeaningOfHawthornLegacyCMSPage',
      component: RedirectToMeaningOfHawthornBlogArticlePage,
    },
    {
      path: '/p/meaning-of-lily-of-the-valley',
      name: 'MeaningOfLilyOfTheValleyLegacyCMSPage',
      component: RedirectToMeaningOfLilyOfTheValleyBlogArticlePage,
    },
    {
      path: '/p/lily-flower-meaning',
      name: 'LilyFlowerMeaningLegacyCMSPage',
      component: RedirectToMeaningOfWhiteLiliesBlogArticlePage,
    },
    {
      path: '/p/lily-flowers',
      name: 'LilyFlowersLegacyCMSPage',
      component: RedirectToMeaningOfWhiteLiliesBlogArticlePage,
    },
    {
      path: '/p/meaning-of-honeysuckle',
      name: 'MeaningOfHoneysuckleLegacyCMSPage',
      component: RedirectToMeaningOfHoneysuckleBlogArticlePage,
    },
    {
      path: '/p/meaning-of-rose',
      name: 'MeaningOfRoseLegacyCMSPage',
      component: RedirectToMeaningOfRoseBlogArticlePage,
    },
    {
      path: '/p/meaning-of-water-lily',
      name: 'MeaningOfWaterLilyLegacyCMSPage',
      component: RedirectToMeaningOfWaterLilyBlogArticlePage,
    },
    {
      path: '/p/meaning-of-larkspur',
      name: 'MeaningOfLarkspurLegacyCMSPage',
      component: RedirectToMeaningOfLarkspurBlogArticlePage,
    },
    {
      path: '/p/meaning-of-poppy',
      name: 'MeaningOfPoppyLegacyCMSPage',
      component: RedirectToMeaningOfPoppyBlogArticlePage,
    },
    {
      path: '/p/meaning-of-gladiolus',
      name: 'MeaningOfGladiolusLegacyCMSPage',
      component: RedirectToMeaningOfGladiolusBlogArticlePage,
    },
    {
      path: '/p/meaning-of-morning-glory',
      name: 'MeaningOfMorningGloryLegacyCMSPage',
      component: RedirectToMeaningOfMorningGloryBlogArticlePage,
    },
    {
      path: '/p/meaning-of-aster',
      name: 'MeaningOfAsterLegacyCMSPage',
      component: RedirectToMeaningOfAsterBlogArticlePage,
    },
    {
      path: '/p/meaning-of-cosmos',
      name: 'MeaningOfCosmosLegacyCMSPage',
      component: RedirectToMeaningOfCosmosBlogArticlePage,
    },
    {
      path: '/p/meaning-of-marigold',
      name: 'MeaningOfMarigoldLegacyCMSPage',
      component: RedirectToMeaningOfMarigoldBlogArticlePage,
    },
    {
      path: '/p/meaning-of-peony',
      name: 'MeaningOfPeonyLegacyCMSPage',
      component: RedirectToMeaningOfPeonyBlogArticlePage,
    },
    {
      path: '/p/meaning-of-chrysanthemum',
      name: 'MeaningOfChrysanthemumLegacyCMSPage',
      component: RedirectToMeaningOfChrysanthemumBlogArticlePage,
    },
    {
      path: '/p/meaning-of-holly',
      name: 'MeaningOfHollyLegacyCMSPage',
      component: RedirectToMeaningOfHollyBlogArticlePage,
    },
    {
      path: '/p/meaning-of-narcissus',
      name: 'MeaningOfNarcissusLegacyCMSPage',
      component: RedirectToMeaningOfNarcissusBlogArticlePage,
    },
    {
      path: '/p/blog-top-10-flower-shops-toronto-2025',
      name: 'TopFlowerShopsToronto2025LegacyCMSPage',
      component: RedirectToTopFlowerShopsToronto2026BlogArticlePage,
    },
    {
      path: '/p/january-birth-flower',
      name: 'JanuaryBirthFlowerLegacyCMSPage',
      component: RedirectToJanuaryBirthFlowerBlogArticlePage,
    },
    {
      path: '/p/february-birth-flower',
      name: 'FebruaryBirthFlowerLegacyCMSPage',
      component: RedirectToFebruaryBirthFlowerBlogArticlePage,
    },
    {
      path: '/p/march-birth-flower',
      name: 'MarchBirthFlowerLegacyCMSPage',
      component: RedirectToMarchBirthFlowerBlogArticlePage,
    },
    {
      path: '/p/april-birth-flower',
      name: 'AprilBirthFlowerLegacyCMSPage',
      component: RedirectToAprilBirthFlowerBlogArticlePage,
    },
    {
      path: '/p/may-birth-flower',
      name: 'MayBirthFlowerLegacyCMSPage',
      component: RedirectToMayBirthFlowerBlogArticlePage,
    },
    {
      path: '/p/june-birth-flower',
      name: 'JuneBirthFlowerLegacyCMSPage',
      component: RedirectToJuneBirthFlowerBlogArticlePage,
    },
    {
      path: '/p/july-birth-flower',
      name: 'JulyBirthFlowerLegacyCMSPage',
      component: RedirectToJulyBirthFlowerBlogArticlePage,
    },
    {
      path: '/p/august-birth-flower',
      name: 'AugustBirthFlowerLegacyCMSPage',
      component: RedirectToAugustBirthFlowerBlogArticlePage,
    },
    {
      path: '/p/september-birth-flower',
      name: 'SeptemberBirthFlowerLegacyCMSPage',
      component: RedirectToSeptemberBirthFlowerBlogArticlePage,
    },
    {
      path: '/p/october-birth-flower',
      name: 'OctoberBirthFlowerLegacyCMSPage',
      component: RedirectToOctoberBirthFlowerBlogArticlePage,
    },
    {
      path: '/p/november-birth-flower',
      name: 'NovemberBirthFlowerLegacyCMSPage',
      component: RedirectToNovemberBirthFlowerBlogArticlePage,
    },
    {
      path: '/p/december-birth-flower',
      name: 'DecemberBirthFlowerLegacyCMSPage',
      component: RedirectToDecemberBirthFlowerBlogArticlePage,
    },
    {
      path: '/p/rose-delivery',
      name: 'RoseDeliveryHubLegacyCMSPage',
      component: RedirectToRoseDeliveryHubPage,
    },
    {
      path: '/p/roses',
      name: 'RosesLegacyCMSPage',
      component: RedirectToRoseDeliveryHubPage,
    },
    {
      path: '/p/roses-delivery-mississauga',
      name: 'MississaugaRosesDeliveryLegacyCMSPage',
      component: RedirectToMississaugaRoseDeliveryPage,
    },
    {
      path: '/p/roses-delivery-vaughan',
      name: 'VaughanRosesDeliveryLegacyCMSPage',
      component: RedirectToVaughanRoseDeliveryPage,
    },
    {
      path: '/p/roses-delivery-markham',
      name: 'MarkhamRosesDeliveryLegacyCMSPage',
      component: RedirectToMarkhamRoseDeliveryPage,
    },
    {
      path: '/p/roses-delivery-richmond-hill',
      name: 'RichmondHillRosesDeliveryLegacyCMSPage',
      component: RedirectToRichmondHillRoseDeliveryPage,
    },
    {
      path: '/p/roses-delivery-brampton',
      name: 'BramptonRosesDeliveryLegacyCMSPage',
      component: RedirectToBramptonRoseDeliveryPage,
    },
    {
      path: '/p/roses-delivery-burlington',
      name: 'BurlingtonRosesDeliveryLegacyCMSPage',
      component: RedirectToBurlingtonRoseDeliveryPage,
    },
    {
      path: '/p/roses-delivery-north-york',
      name: 'NorthYorkRosesDeliveryLegacyCMSPage',
      component: RedirectToNorthYorkRoseDeliveryPage,
    },
    {
      path: '/p/roses-delivery-scarborough',
      name: 'ScarboroughRosesDeliveryLegacyCMSPage',
      component: RedirectToScarboroughRoseDeliveryPage,
    },
    {
      path: '/p/roses-delivery-oakville',
      name: 'OakvilleRosesDeliveryLegacyCMSPage',
      component: RedirectToOakvilleRoseDeliveryPage,
    },
    {
      path: '/p/roses-delivery-etobicoke',
      name: 'EtobicokeRosesDeliveryLegacyCMSPage',
      component: RedirectToEtobicokeRoseDeliveryPage,
    },
    {
      path: '/p/roses-delivery-toronto',
      name: 'TorontoRosesDeliveryLegacyCMSPage',
      component: RedirectToTorontoRoseDeliveryPage,
    },
    {
      path: '/p/roses-delivery-toronto-types-colors-meanings',
      name: 'TorontoRosesTypesLegacyCMSPage',
      component: RedirectToTorontoRoseDeliveryPage,
    },
    {
      path: '/p/red-rose-toronto',
      name: 'TorontoRedRosesLegacyCMSPage',
      component: RedirectToTorontoRedRosesDeliveryPage,
    },
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    {
      path: '/flower-delivery',
      name: 'FlowerDeliveryHubPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(flowerDeliveryHubSeoPageId),
      extraProps: { seoPageId: flowerDeliveryHubSeoPageId },
    },
    {
      path: '/gift-delivery',
      name: 'GiftDeliveryHubPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(giftDeliveryHubSeoPageId),
      extraProps: { seoPageId: giftDeliveryHubSeoPageId },
    },
    {
      path: '/gift-delivery/gourmet-sets',
      name: 'GourmetSetsDeliveryHubPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(gourmetSetsDeliveryHubSeoPageId),
      extraProps: { seoPageId: gourmetSetsDeliveryHubSeoPageId },
    },
    {
      path: '/gift-delivery/mississauga',
      name: 'MississaugaGiftDeliveryHubPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaGiftDeliveryHubSeoPageId),
      extraProps: { seoPageId: mississaugaGiftDeliveryHubSeoPageId },
    },
    {
      path: '/gift-delivery/toronto',
      name: 'TorontoGiftDeliveryHubPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoGiftDeliveryHubSeoPageId),
      extraProps: { seoPageId: torontoGiftDeliveryHubSeoPageId },
    },
    {
      path: '/flower-delivery/port-credit',
      name: 'PortCreditFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(portCreditSeoPageId),
      extraProps: { seoPageId: portCreditSeoPageId },
    },
    {
      path: '/flower-delivery/mississauga',
      name: 'MississaugaFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaFlowerDeliverySeoPageId),
      extraProps: { seoPageId: mississaugaFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/toronto',
      name: 'TorontoFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoFlowerDeliverySeoPageId),
      extraProps: { seoPageId: torontoFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/toronto/birthday-flowers',
      name: 'TorontoBirthdayFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoBirthdayFlowersSeoPageId),
      extraProps: { seoPageId: torontoBirthdayFlowersSeoPageId },
    },
    {
      path: '/flower-delivery/toronto/anniversary-flowers',
      name: 'TorontoAnniversaryFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoAnniversaryFlowersSeoPageId),
      extraProps: { seoPageId: torontoAnniversaryFlowersSeoPageId },
    },
    {
      path: '/flower-delivery/toronto/mothers-day-flowers',
      name: 'TorontoMothersDayFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoMothersDayFlowersSeoPageId),
      extraProps: { seoPageId: torontoMothersDayFlowersSeoPageId },
    },
    {
      path: '/flower-delivery/toronto/get-well-flowers',
      name: 'TorontoGetWellFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoGetWellFlowersSeoPageId),
      extraProps: { seoPageId: torontoGetWellFlowersSeoPageId },
    },
    {
      path: '/flower-delivery/toronto/thank-you-flowers',
      name: 'TorontoThankYouFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoThankYouFlowersSeoPageId),
      extraProps: { seoPageId: torontoThankYouFlowersSeoPageId },
    },
    {
      path: '/flower-delivery/toronto/wedding-flowers',
      name: 'TorontoWeddingFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoWeddingFlowersSeoPageId),
      extraProps: { seoPageId: torontoWeddingFlowersSeoPageId },
    },
    {
      path: '/flower-delivery/vaughan',
      name: 'VaughanFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(vaughanFlowerDeliverySeoPageId),
      extraProps: { seoPageId: vaughanFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/markham',
      name: 'MarkhamFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(markhamFlowerDeliverySeoPageId),
      extraProps: { seoPageId: markhamFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/richmond-hill',
      name: 'RichmondHillFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(richmondHillFlowerDeliverySeoPageId),
      extraProps: { seoPageId: richmondHillFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/brampton',
      name: 'BramptonFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(bramptonFlowerDeliverySeoPageId),
      extraProps: { seoPageId: bramptonFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/burlington',
      name: 'BurlingtonFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(burlingtonFlowerDeliverySeoPageId),
      extraProps: { seoPageId: burlingtonFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/north-york',
      name: 'NorthYorkFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(northYorkFlowerDeliverySeoPageId),
      extraProps: { seoPageId: northYorkFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/scarborough',
      name: 'ScarboroughFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(scarboroughFlowerDeliverySeoPageId),
      extraProps: { seoPageId: scarboroughFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/oakville',
      name: 'OakvilleFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(oakvilleFlowerDeliverySeoPageId),
      extraProps: { seoPageId: oakvilleFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/etobicoke',
      name: 'EtobicokeFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(etobicokeFlowerDeliverySeoPageId),
      extraProps: { seoPageId: etobicokeFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/clarkson',
      name: 'ClarksonFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(clarksonFlowerDeliverySeoPageId),
      extraProps: { seoPageId: clarksonFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/lakeview',
      name: 'LakeviewFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(lakeviewFlowerDeliverySeoPageId),
      extraProps: { seoPageId: lakeviewFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/cooksville',
      name: 'CooksvilleFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(cooksvilleFlowerDeliverySeoPageId),
      extraProps: { seoPageId: cooksvilleFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/streetsville',
      name: 'StreetsvilleFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(streetsvilleFlowerDeliverySeoPageId),
      extraProps: { seoPageId: streetsvilleFlowerDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/erin-mills',
      name: 'ErinMillsFlowerDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(erinMillsFlowerDeliverySeoPageId),
      extraProps: { seoPageId: erinMillsFlowerDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/mississauga/cakes',
      name: 'MississaugaCakesDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaCakesDeliverySeoPageId),
      extraProps: { seoPageId: mississaugaCakesDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/mississauga/chocolate-covered-strawberries',
      name: 'MississaugaChocolateCoveredStrawberriesDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaChocolateCoveredStrawberriesDeliverySeoPageId),
      extraProps: { seoPageId: mississaugaChocolateCoveredStrawberriesDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/mississauga/balloons',
      name: 'MississaugaBalloonsDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaBalloonsDeliverySeoPageId),
      extraProps: { seoPageId: mississaugaBalloonsDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/mississauga/balloon-arch',
      name: 'MississaugaBalloonArchDecorPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaBalloonArchDecorSeoPageId),
      extraProps: { seoPageId: mississaugaBalloonArchDecorSeoPageId },
    },
    {
      path: '/gift-delivery/mississauga/gourmet-sets',
      name: 'MississaugaGourmetSetsDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaGourmetSetsDeliverySeoPageId),
      extraProps: { seoPageId: mississaugaGourmetSetsDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/mississauga/indoor-plants',
      name: 'MississaugaIndoorPlantsDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaIndoorPlantsDeliverySeoPageId),
      extraProps: { seoPageId: mississaugaIndoorPlantsDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/toronto/cakes',
      name: 'TorontoCakesDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoCakesDeliverySeoPageId),
      extraProps: { seoPageId: torontoCakesDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/toronto/bento-cakes',
      name: 'TorontoBentoCakesDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoBentoCakesDeliverySeoPageId),
      extraProps: { seoPageId: torontoBentoCakesDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/toronto/chocolate-covered-strawberries',
      name: 'TorontoChocolateCoveredStrawberriesDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoChocolateCoveredStrawberriesDeliverySeoPageId),
      extraProps: { seoPageId: torontoChocolateCoveredStrawberriesDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/toronto/balloons',
      name: 'TorontoBalloonsDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoBalloonsDeliverySeoPageId),
      extraProps: { seoPageId: torontoBalloonsDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/toronto/balloon-arch',
      name: 'TorontoBalloonArchDecorPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoBalloonArchDecorSeoPageId),
      extraProps: { seoPageId: torontoBalloonArchDecorSeoPageId },
    },
    {
      path: '/gift-delivery/toronto/balloon-garland',
      name: 'TorontoBalloonGarlandDecorPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoBalloonGarlandDecorSeoPageId),
      extraProps: { seoPageId: torontoBalloonGarlandDecorSeoPageId },
    },
    {
      path: '/gift-delivery/toronto/birthday-balloon-decor',
      name: 'TorontoBirthdayBalloonDecorPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoBirthdayBalloonDecorSeoPageId),
      extraProps: { seoPageId: torontoBirthdayBalloonDecorSeoPageId },
    },
    {
      path: '/gift-delivery/toronto/baby-shower-decorations',
      name: 'TorontoBabyShowerDecorPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoBabyShowerDecorSeoPageId),
      extraProps: { seoPageId: torontoBabyShowerDecorSeoPageId },
    },
    {
      path: '/gift-delivery/toronto/baby-boy-shower-decorations',
      name: 'TorontoBabyBoyShowerDecorPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoBabyBoyShowerDecorSeoPageId),
      extraProps: { seoPageId: torontoBabyBoyShowerDecorSeoPageId },
    },
    {
      path: '/gift-delivery/toronto/baby-girl-shower-decorations',
      name: 'TorontoBabyGirlShowerDecorPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoBabyGirlShowerDecorSeoPageId),
      extraProps: { seoPageId: torontoBabyGirlShowerDecorSeoPageId },
    },
    {
      path: '/gift-delivery/toronto/bridal-shower-decorations',
      name: 'TorontoBridalShowerDecorPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoBridalShowerDecorSeoPageId),
      extraProps: { seoPageId: torontoBridalShowerDecorSeoPageId },
    },
    {
      path: '/gift-delivery/toronto/gourmet-sets',
      name: 'TorontoGourmetSetsDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoGourmetSetsDeliverySeoPageId),
      extraProps: { seoPageId: torontoGourmetSetsDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/toronto/christmas-gift-baskets',
      name: 'TorontoChristmasGiftBasketsDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoChristmasGiftBasketsDeliverySeoPageId),
      extraProps: { seoPageId: torontoChristmasGiftBasketsDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/toronto/corporate-gift-baskets',
      name: 'TorontoCorporateGiftBasketsDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoCorporateGiftBasketsDeliverySeoPageId),
      extraProps: { seoPageId: torontoCorporateGiftBasketsDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/toronto/thank-you-gift-baskets',
      name: 'TorontoThankYouGiftBasketsDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoThankYouGiftBasketsDeliverySeoPageId),
      extraProps: { seoPageId: torontoThankYouGiftBasketsDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/toronto/housewarming-gift-baskets',
      name: 'TorontoHousewarmingGiftBasketsDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoHousewarmingGiftBasketsDeliverySeoPageId),
      extraProps: { seoPageId: torontoHousewarmingGiftBasketsDeliverySeoPageId },
    },
    {
      path: '/gift-delivery/toronto/indoor-plants',
      name: 'TorontoIndoorPlantsDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoIndoorPlantsDeliverySeoPageId),
      extraProps: { seoPageId: torontoIndoorPlantsDeliverySeoPageId },
    },
    {
      path: '/rose-delivery',
      name: 'RoseDeliveryHubPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(roseDeliveryHubSeoPageId),
      extraProps: { seoPageId: roseDeliveryHubSeoPageId },
    },
    {
      path: '/rose-delivery/mississauga',
      name: 'MississaugaRoseDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaRoseDeliverySeoPageId),
      extraProps: { seoPageId: mississaugaRoseDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/vaughan',
      name: 'VaughanRoseDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(vaughanRoseDeliverySeoPageId),
      extraProps: { seoPageId: vaughanRoseDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/markham',
      name: 'MarkhamRoseDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(markhamRoseDeliverySeoPageId),
      extraProps: { seoPageId: markhamRoseDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/richmond-hill',
      name: 'RichmondHillRoseDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(richmondHillRoseDeliverySeoPageId),
      extraProps: { seoPageId: richmondHillRoseDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/brampton',
      name: 'BramptonRoseDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(bramptonRoseDeliverySeoPageId),
      extraProps: { seoPageId: bramptonRoseDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/burlington',
      name: 'BurlingtonRoseDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(burlingtonRoseDeliverySeoPageId),
      extraProps: { seoPageId: burlingtonRoseDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/north-york',
      name: 'NorthYorkRoseDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(northYorkRoseDeliverySeoPageId),
      extraProps: { seoPageId: northYorkRoseDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/scarborough',
      name: 'ScarboroughRoseDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(scarboroughRoseDeliverySeoPageId),
      extraProps: { seoPageId: scarboroughRoseDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/oakville',
      name: 'OakvilleRoseDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(oakvilleRoseDeliverySeoPageId),
      extraProps: { seoPageId: oakvilleRoseDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/etobicoke',
      name: 'EtobicokeRoseDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(etobicokeRoseDeliverySeoPageId),
      extraProps: { seoPageId: etobicokeRoseDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/toronto',
      name: 'TorontoRoseDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoRoseDeliverySeoPageId),
      extraProps: { seoPageId: torontoRoseDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/toronto/red-roses',
      name: 'TorontoRedRosesDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoRedRosesDeliverySeoPageId),
      extraProps: { seoPageId: torontoRedRosesDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/toronto/white-roses',
      name: 'TorontoWhiteRosesDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoWhiteRosesDeliverySeoPageId),
      extraProps: { seoPageId: torontoWhiteRosesDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/toronto/pink-roses',
      name: 'TorontoPinkRosesDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoPinkRosesDeliverySeoPageId),
      extraProps: { seoPageId: torontoPinkRosesDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/toronto/100-roses',
      name: 'TorontoHundredRosesDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoHundredRosesDeliverySeoPageId),
      extraProps: { seoPageId: torontoHundredRosesDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/toronto/cheap-roses',
      name: 'TorontoCheapRosesDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoCheapRosesDeliverySeoPageId),
      extraProps: { seoPageId: torontoCheapRosesDeliverySeoPageId },
    },
    {
      path: '/rose-delivery/toronto/12-roses',
      name: 'TorontoTwelveRosesDeliveryPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(torontoTwelveRosesDeliverySeoPageId),
      extraProps: { seoPageId: torontoTwelveRosesDeliverySeoPageId },
    },
    {
      path: '/flower-delivery/mississauga/birthday-flowers',
      name: 'MississaugaBirthdayFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaBirthdayFlowersSeoPageId),
      extraProps: { seoPageId: mississaugaBirthdayFlowersSeoPageId },
    },
    {
      path: '/flower-delivery/mississauga/sympathy-flowers',
      name: 'MississaugaSympathyFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaSympathyFlowersSeoPageId),
      extraProps: { seoPageId: mississaugaSympathyFlowersSeoPageId },
    },
    {
      path: '/flower-delivery/mississauga/anniversary-flowers',
      name: 'MississaugaAnniversaryFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaAnniversaryFlowersSeoPageId),
      extraProps: { seoPageId: mississaugaAnniversaryFlowersSeoPageId },
    },
    {
      path: '/flower-delivery/mississauga/mothers-day-flowers',
      name: 'MississaugaMothersDayFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaMothersDayFlowersSeoPageId),
      extraProps: { seoPageId: mississaugaMothersDayFlowersSeoPageId },
    },
    {
      path: '/flower-delivery/mississauga/valentines-day-flowers',
      name: 'MississaugaValentinesDayFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaValentinesDayFlowersSeoPageId),
      extraProps: { seoPageId: mississaugaValentinesDayFlowersSeoPageId },
    },
    {
      path: '/flower-delivery/mississauga/get-well-flowers',
      name: 'MississaugaGetWellFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaGetWellFlowersSeoPageId),
      extraProps: { seoPageId: mississaugaGetWellFlowersSeoPageId },
    },
    {
      path: '/flower-delivery/mississauga/wedding-flowers',
      name: 'MississaugaWeddingFlowersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(mississaugaWeddingFlowersSeoPageId),
      extraProps: { seoPageId: mississaugaWeddingFlowersSeoPageId },
    },
    {
      path: '/flower-delivery-port-credit',
      name: 'PortCreditFlowerDeliveryLegacyPage',
      component: RedirectToPortCreditFlowerDeliveryPage,
    },
    // NOTE: when the private marketplace feature is enabled, the '/s' route is disallowed by the robots.txt resource.
    // If you add new routes that start with '/s*' (e.g. /support), you should add them to the robotsPrivateMarketplace.txt file.
    {
      path: '/s',
      name: 'SearchPage',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/s/:listingType',
      name: 'SearchPageWithListingType',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      component: RedirectToCart,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      component: RedirectToShopPortal,
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      component: RedirectToShopPortal,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      component: RedirectToShopPortal,
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      component: RedirectProfileToStore,
    },
    {
      path: '/u/:id/:variant',
      name: 'ProfilePageVariant',
      component: RedirectProfileToStore,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      component: RedirectToCustomerAccount,
    },

    // Shops sign in with a password at /my-shop; customers use the
    // passwordless link at /account. The old Sharetribe auth paths stay as
    // redirects for bookmarks and external links.
    {
      path: '/login',
      name: 'LoginPage',
      component: RedirectToShopPortal,
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: RedirectToShopPortal,
    },
    {
      path: '/signup/:userType',
      name: 'SignupForUserTypePage',
      component: RedirectToShopPortal,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: RedirectToShopPortal,
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: RedirectToShopPortal,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      component: RedirectToShopPortal,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      component: RedirectInbox,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      component: RedirectToCustomerAccount,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      component: RedirectToCustomerAccount,
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      component: RedirectToShopPortal,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      component: RedirectToShopPortal,
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      component: RedirectToShopPortal,
    },
    {
      // '/account' itself is CustomerAccountPage (declared above); this name
      // is still used by the topbar's active-menu check.
      path: '/account/settings',
      name: 'AccountSettingsPage',
      component: RedirectToCustomerAccount,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      component: RedirectToCustomerAccount,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      component: RedirectToShopPortal,
    },
    {
      path: '/account/payments',
      name: 'StripePayoutPage',
      component: RedirectToShopPortal,
    },
    {
      path: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      component: RedirectToShopPortal,
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      component: RedirectToCustomerAccount,
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
      loadData: pageDataLoadingAPI.TermsOfServicePage.loadData,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      path: '/partners',
      name: 'PartnersPage',
      component: SeoLocationPage,
      loadData: seoLocationPageLoadData(partnersSeoPageId),
      extraProps: { seoPageId: partnersSeoPageId },
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/no-:missingAccessRight',
      name: 'NoAccessPage',
      component: NoAccessPage,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Old Sharetribe e-mail links (password reset / e-mail verification).
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: RedirectToShopPortal,
    },
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      component: RedirectToCustomerAccount,
    },
  ];
};

export default routeConfiguration;
